/* DeviceDetailsViewRenderer.js */

"use strict";
const _ = require("lodash");
const ButtonControl = require("@gluenetworks/steve_ui_core").Control.Action.Button;
const SiteCFUtility = require("../../../../lib/SiteCfUtility");
const childViewComponent = require("../../../../widgets/childview/ChildViewRenderer");
const haGroupComponent = require("./subviews/ha_group/HaGroupViewComponent");
const InheritedFieldUtility = require("../../../../lib/InheritedFieldUtility");

const DeviceEditorGroups = require("../../../../widgets/editorgroups/EditorGroupsComponent");

// OS Family options for form dropdown
const OS_FAMILY_OPTIONS = [{Windows: "Windows"}, {Debian: "Debian"}];

const moment = require("moment");
require("moment-duration-format");

const readOnlyCustomFieldIds = [
    "2abc8e82-4631-4fa1-ab66-d414bb4e8e05", // criticalAdvisoryCustomFieldId
    "9ed3ecea-82a4-4141-8d12-f41ec7e04367", // highAdvisoryCustomFieldId
    "8c467f86-c54c-450a-a2b8-c4f57a78b82d", // mediumAdvisoryCustomFieldId
    "118d6b5c-1d81-4aa3-a2d8-ad50585ff79b", // bulletinNumberCustomFieldId
    "092f60e9-d47f-469e-abbf-004781107833", // contractNumberCustomFieldId
    "0c6644ce-a700-476a-82f9-16e4821ed430", // coverageEndCustomFieldId
    "43236483-c177-42f1-aed5-b972ebe23eb8", // productIdCustomFieldId
    "be30fec7-358d-45b5-9aca-32ac70df15c1", // endOfSaleCustomFieldId
    "779012d1-0ec3-49c0-9153-89244d31ba40", // endOfMaintenanceCustomFieldId
    "49026bc7-b6a1-4b0d-885d-c7c7f60f7dec", // endOfSupportCustomFieldId
    "d183ab5c-a616-4582-97dc-d6b18ab750db", // Meraki Org ID
    "a339c6d8-b3fb-482d-bd0a-4e924ec609bf", // Meraki Org
    "b49c0eb0-4863-4518-8582-e9e1fbde149b", // Meraki Network ID
    "73dea023-740e-4329-a3a0-5ced9bac3700", // Meraki Network"
    "4c9979d6-2c80-42f6-812f-37445447aa1c", // Meraki Stack ID
    "362c6a54-8cc8-4c30-b78f-1263cceb9a0e", // Meraki Stack Name
];

const readOnlyFieldIds = [
    "latestHaveIChangedLastChecked",
    "latestHaveIChanged",
    "capturedBy",
    "discoveredOn",
    "discoveryStatus",
    "offlineDiscoveryStatus",
    "discoveredBy",
    "detectedVendor",
    "detectedHostName",
    "detectedOs",
    "detectedSoftwareVersion",
    "detectedSerialNumber",
    "serialNumber",
    "detectedSku",
    "detectedUptime",
    "detectedComponents", // Only for the top object, sub objects need to be made dynamically read-only
    "detectedPhoneModels",
    "detectedChassis",
    "detectedStack",
    "detectedStackName",
    "detectedStackId",
    "elementCount",
    "totalCount",
    "useCount",
    "freeCount",
    "usePercent",
    "freePercent",
    "configuredBy",
    "configuredInfo",
    "configuredOn",
    "configuredType",
    "rebootedBy",
    "rebootedOn",
    "updatedOn",
    "updatedBy",
    "accessStatus",
    "lastActivityOn",
    "lastActivityBy",
    "lastAuditPolicy",
    "lastActivityType",
    "lastActivityError",
    "auditStatus",
    "auditedBy",
    "auditedOn",
    "fileServerIdentifier",
    "fileServerName",
    "osUpgradeStatus",
    "osUpgradedBy",
    "osUpgradedOn",
    "osUpgradeError",
    "osUpgradePlanId",
    "osUpgradePlanName",
    "osUpgradePlanAction",
    "componentEox",
    "detectedPorts",
    "portSummary",
    "layer2PortsTotalCount",
    "layer2PortsUseCount",
    "layer2PortsFreeCount",
    "layer2PortsUsePercent",
    "layer2PortsFreePercent",
    "layer3PortsTotalCount",
    "layer3PortsUseCount",
    "layer3PortsFreeCount",
    "layer3PortsUsePercent",
    "layer3PortsFreePercent",
    "licenseCount",
    "rebootedBy",
    "rebootedOn",
    "discoveredMacAddresses",
    "detectedLicenses",
    "nodeId",
    "nodeTypeDisplayName",
    "nodeProps",
    "detectedRawTypeBase",
    "detectedTypeName",
    "detectedRawOs",
    "detectedRawBase",
    "detectedRawVendor",
    "siteCodeName",
    "siteLocationType",
    "controlType",
    "controllerName",
    "connectionMethodLocked",
    "lastControlledAddCount",
    "lastControlledUpdateCount",
    "controlledDeviceCount",
    "configDrifted",
    "previouslyDetectedConnectionData",
    "latestConfig",
    "credsName",
    "rawDeviceInfo",
    "cmdbCiId",
    "cmdbCiName",
    "cmdbClassName",
    "cmdbSyncStatus",
    "lastCmdbSyncBy",
    "lastCmdbSyncError",
    "lastCmdbSyncOn",
    "macAddresses",
    "serialNumber",
    "discoveryUploadedBy",
    "offlineDiscoveredOn",
    "discoveryUploadedOn",
    "offlineDiscoveryError",
    // "haPairName"  // Case 25550 - haPair is really not a read only value
    // "detectedTypeBase",  // Discovered property but which is not read-only in device form
];

const clusterColumns = {
    _id: {
        label: "Name",
        showSort: true,
        priority: 1,
    },

    podId: {
        label: "POD ID",
        showSort: true,
        priority: 2,
    },

    elementId: {
        label: "Node ID",
        showSort: true,
        priority: 3,
    },

    ipAddress: {
        label: "IP Address",
        showSort: true,
        priority: 4,
    },

    serialNumber: {
        label: "Serial",
        showSort: true,
        priority: 5,
        hidden: false,
    },

    hostname: {
        label: "Hostname",
        showSort: true,
        priority: 6,
    },

    os: {
        label: "OS",
        showSort: true,
        priority: 7,
    },

    version: {
        label: "OS Version",
        showSort: true,
        priority: 8,
        path: "software.version",
    },

    uptime: {
        label: "Uptime",
        showSort: true,
        priority: 9,
    },
};

class DeviceDetailsViewRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("device-details-view-gl");
        this._rootElem.component = this._model;
        this._logger = this._model.logger;
        this._isDirty = false;

        this._formsComponent = {};
        this._siteCfUtility = new SiteCFUtility();

        this._model.gameEditor.addViewAction({
            type: "normal",
            displayName: "Save",
            name: "SaveDeviceDetails",
            handler: (this._onDeviceDetailsSave = this._onDeviceDetailsSave.bind(this)),
        });
        this._model.gameEditor.addViewAction({
            type: "normal",
            displayName: "Cancel",
            name: "CancelDeviceDetails",
            handler: (this._onDeviceDetailsCancel = this._onDeviceDetailsCancel.bind(this)),
        });

        this._viewHAContainer = document.createElement("view-button-container");
        this._viewHAButton = new ButtonControl({
            label: "Add",
            darkTheme: false,
        });
        this._removeHAButton = new ButtonControl({
            label: "Remove",
            darkTheme: false,
            class: "remove_ha",
        });
        this._viewHAContainer.appendChild(this._viewHAButton.renderer.rootElement);
        this._viewHAContainer.appendChild(this._removeHAButton.renderer.rootElement);

        this._renderingForm = false;

        this._debouncedOnDevicePropertiesChanged = _.debounce(
            this._onDevicePropertiesChanged.bind(this),
            1e3,
            false
        );

        this._debouncedOnExtendedPropertiesChanged = _.debounce(
            this._onExtendedPropertiesChanged.bind(this),
            1e3,
            false
        );

        this._deviceEditorGroups = {};

        this._toggleActions();
        this._registerListeners();
    }

    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/

    _registerListeners() {
        this._model.on("Dirty", (this._onDirty = this._onDirty.bind(this)));
        this._model.on("DevicePropertiesChanged", this._debouncedOnDevicePropertiesChanged);
        this._model.on("ExtendedPropertiesChanged", this._debouncedOnExtendedPropertiesChanged);
        this._model.on(
            "OsImageServerFeedUpdated",
            (this._onOsImageServerFeedUpdated = this._onOsImageServerFeedUpdated.bind(this))
        );
        this._model.on(
            "SiteFeedUpdated",
            (this._onSiteFeedUpdated = this._onSiteFeedUpdated.bind(this))
        );
        this._model.on(
            "EngineZoneFeedUpdate",
            (this._onEngineZoneFeedUpdated = this._onEngineZoneFeedUpdated.bind(this))
        );
        this._model.on(
            "PlatformOptionsUpdated",
            (this._onPlatformOptionsUpdated = this._onPlatformOptionsUpdated.bind(this))
        );
        this._model.on(
            "TagOptionsUpdated",
            (this._onTagOptionsUpdated = this._onTagOptionsUpdated.bind(this))
        );
        this._model.on(
            "OrgZoneFeedUpdate",
            (this._onOrgZoneFeedUpdated = this._onOrgZoneFeedUpdated.bind(this))
        );
        this._model.on(
            "OdmValueMapFeedUpdated",
            (this._onOdmValueMapFeedUpdated = this._onOdmValueMapFeedUpdated.bind(this))
        );

        this._viewHAButton.on(
            "UserClick",
            (this._onViewHAClick = _.debounce(_.bind(this._onViewHAClick, this), 3000, {
                maxWait: 3000,
                leading: true,
                trailing: false,
            }))
        );
        this._removeHAButton.on(
            "UserClick",
            (this._onRemoveHAClick = this._onRemoveHAClick.bind(this))
        );

        this._model.on(
            "CustomFieldUpdated",
            (this._onCustomFieldUpdated = this._onCustomFieldUpdated.bind(this))
        );
        this._model.on(
            "HAPairPatch",
            (this._updateHAPairFormVal = this._updateHAPairFormVal.bind(this))
        ); // Case 25550

        this._model.on(
            "ExtendedPropsTypesAddedOrRemoved",
            (this._edpAddedOrRemoved = this._edpAddedOrRemoved.bind(this))
        );
    }

    _registerEditorGroupsEvents() {
        this._deviceEditorGroups.on(
            "PendingPatchesChanged",
            (this._onFormsPendingPatchesChanged = this._onFormsPendingPatchesChanged.bind(this))
        );
    }

    _unregisterEditorGroupsEvents() {
        if (this._deviceEditorGroups) {
            this._deviceEditorGroups.removeListener(
                "PendingPatchesChanged",
                this._onFormsPendingPatchesChanged
            );
        }
    }

    _unregisterListeners() {
        this._model.removeListener("Dirty", this._onDirty);
        this._model.removeListener(
            "DevicePropertiesChanged",
            this._debouncedOnDevicePropertiesChanged
        );
        this._model.removeListener(
            "ExtendedPropertiesChanged",
            this._debouncedOnExtendedPropertiesChanged
        );
        this._model.removeListener("HAPairPatch", this._updateHAPairFormVal);
        this._model.removeListener("OsImageServerFeedUpdated", this._onOsImageServerFeedUpdated);
        this._model.removeListener("SiteFeedUpdated", this._onSiteFeedUpdated);
        this._model.removeListener("EngineZoneFeedUpdate", this._onEngineZoneFeedUpdated);
        this._model.removeListener("PlatformOptionsUpdated", this._onPlatformOptionsUpdated);
        this._model.removeListener("TagOptionsUpdated", this._onTagOptionsUpdated);
        this._model.removeListener("OrgZoneFeedUpdate", this._onOrgZoneFeedUpdated);
        this._model.removeListener("OdmValueMapFeedUpdated", this._onOdmValueMapFeedUpdated);

        this._viewHAButton.removeListener("UserClick", this._onViewHAClick);
        this._removeHAButton.removeListener("UserClick", this._onRemoveHAClick);

        this._model.removeListener("CustomFieldUpdated", this._onCustomFieldUpdated);
        this._model.removeListener("ExtendedPropsTypesAddedOrRemoved", this._edpAddedOrRemoved);
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/

    shutdown() {
        this._debouncedOnDevicePropertiesChanged.cancel();
        this._debouncedOnExtendedPropertiesChanged.cancel();
        this._unregisterListeners();
        this._unregisterEditorGroupsEvents();

        let promArr = [this._deviceEditorGroups.shutdown()];

        return Promise.all(promArr).then(
            () => {
                this._model = undefined;
                this._rootElem.remove();
                this._rootElem = undefined;
                this._viewHAContainer = undefined;
                this._deviceEditorGroups = undefined;
            },
            (err) => {
                this._logger.warn({error: err}, "DeviceDetailsViewRenderer.shutdown() error.");
            }
        );
    }

    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render(loadingMessage = "") {
        if (this._rootElem.hasChildNodes()) {
            this._rootElem.innerHTML = "";
        }
        let loadingMessageForVeil = {};
        if (loadingMessage) {
            loadingMessageForVeil = {message: loadingMessage};
        }
        this._model._modalUserFeedback.displayLoading(loadingMessageForVeil);
        return this._model
            .whenModelIsReady()
            .then(() => {
                if (!_.isEmpty(this._deviceEditorGroups) && this._deviceEditorGroups.shutdown) {
                    return this._deviceEditorGroups.shutdown();
                }
                return Promise.resolve();
            })
            .then(() => {
                this._deviceEditorGroups = new DeviceEditorGroups(
                    this._getEntitySchemaObject(),
                    this.getPristineValue(),
                    this._model._userSession,
                    this._model._nibApiService
                );

                this._toggleActions();

                this._renderingForm = true;
                this._rootElem.appendChild(this._deviceEditorGroups.renderer.getRootElement());
                return this.renderDeviceForm();
            })
            .then(() => {
                this._model._modalUserFeedback.hideDisplayed();
            })
            .catch((err) => {
                this._logger.warn({error: err}, "DeviceDetailsViewRenderer.render() error.");
            });
    }

    renderDeviceForm() {
        return this._deviceEditorGroups._renderer
            .render()
            .then(() => {
                this._formsComponent = this._deviceEditorGroups._renderer.formsComponent;
                this._registerEditorGroupsEvents();
                if (this._model._deviceCategory !== "iot") {
                    this._hideConnectionMethodDropDown();
                }

                this._customFieldHierarchyObj = this._siteCfUtility.getCustomFieldInheritance(
                    this._model._device,
                    this._model._parentSite,
                    this._model._customFieldColumnDef
                );

                new Promise((resolve) => {
                    setTimeout(resolve, 500);
                }).then(() => {
                    this._renderingForm = false;
                    if (this._model._deviceCategory !== "iot") {
                        this._updateAccessStatus();
                    }
                    this._setSiteDetails();

                    if (this._model._deviceCategory !== "iot") {
                        this._updateHAGroup();
                        this._discoveryLevelInheritanceUtility = new InheritedFieldUtility(
                            "discoveryLevel",
                            "discovery_level",
                            this._model._device,
                            this._model._parentSite,
                            this._deviceEditorGroups._renderer.getFormsComponent(),
                            undefined,
                            undefined,
                            this._model._siteNamemap
                        );
                        this._discoveryLevelInheritanceUtility.generateIcon();
                    }
                    this._siteCfUtility.updateCustomFieldsHierarchy(
                        this._rootElem,
                        this._model._device,
                        this._deviceEditorGroups._renderer.getFormsComponent()
                    );
                });
            })
            .catch((err) => err);
    }

    get rootElement() {
        return this.getRootElement();
    }

    getRootElement() {
        return this._rootElem;
    }

    get model() {
        return this.getModel();
    }

    getModel() {
        return this._model;
    }

    /*******************************************/
    /* END COMPONENT RENDERER METHODS          */
    /* START PRIVATE ACTION METHODS            */
    /*******************************************/

    _showViewActions() {
        if (this._isDirty) {
            this._model.gameEditor.showViewAction("SaveDeviceDetails");
            this._model.gameEditor.showViewAction("CancelDeviceDetails");
        }
    }

    _hideViewActions() {
        this._model.gameEditor.hideViewAction("SaveDeviceDetails");
        this._model.gameEditor.hideViewAction("CancelDeviceDetails");
    }

    _disableViewActions() {
        this._model.gameEditor.disableViewAction("SaveDeviceDetails");
        this._model.gameEditor.disableViewAction("CancelDeviceDetails");
    }

    _enableViewActions() {
        this._model.gameEditor.enableViewAction("SaveDeviceDetails");
        this._model.gameEditor.enableViewAction("CancelDeviceDetails");
    }

    _toggleActions() {
        if (this._isDirty) {
            this._showViewActions();
        } else {
            this._hideViewActions();
        }
    }
    /*******************************************/
    /* END PRIVATE ACTION METHODS              */
    /* START PRIVATE RENDER METHODS            */
    /*******************************************/

    /*******************************************/
    /* END PRIVATE RENDER METHODS              */
    /* START HANDLER METHODS                   */
    /*******************************************/

    _iotDevicePendingPatchesChanged(form) {
        let customFieldsFromForm = form.customFields ? _.cloneDeep(form.customFields) : {};

        // General
        const formName = form.general.name;

        this._model._device.setName(formName);
        if (!_.isNil(form.general.description)) {
            this._model._device.setDescription(form.general.description);
        }
        if (!_.isNil(form.general.tagIds)) {
            this._model._device.setTagIds(form.general.tagIds);
        }
        this._model._device.setManagementState(form.general.managementState);
        this._model._device.setEnvironment(form.general.environment);

        // Site
        const siteId = this._model._device.getSiteId() || "/";
        if (siteId !== form.site.siteId) {
            if (_.isEmpty(form.site.siteId) || form.site.siteId === "/") {
                this._model._device.clearSiteRelationship();
            } else {
                this._model._device.setSiteRelationship(form.site.siteId);
            }
        } else {
            this._model._gameEditor.clearPendingRelationshipsFor(
                "site",
                this._model._device,
                false
            );
        }
        this._model._device.setAllowSiteAutoAssignment(form.site.allowSiteAutoAssignment);
        this._setSiteDetails();

        // Discovery
        if (!_.isEmpty(form.discovery.detectedTypeBase)) {
            this._model._device.detectedTypeBase = form.discovery.detectedTypeBase;
        }

        // Check if platform selection changed and populate fields from platform
        const currentPlatformId = _.get(this._model._device, "properties.platformId") || null;
        const newPlatformId = form.discovery.platformId || null;

        const defaultFalsyForProperty = (propName) => {
            return this._model._device.pristineProperties[propName]
                ? undefined
                : this._model._device.pristineProperties[propName];
        };

        if (newPlatformId !== currentPlatformId) {
            const selectedPlatform = newPlatformId
                ? this._model.getOdmOsPlatformById(newPlatformId)
                : null;
            if (selectedPlatform) {
                form.discovery.manufacturer = selectedPlatform.manufacturer || "";
                form.discovery.model = selectedPlatform.model || "";
                form.discovery.deviceType = selectedPlatform.deviceType || "";
                form.discovery.osName = selectedPlatform.osName || "";
                form.discovery.osFamily = selectedPlatform.osFamily || "";
                form.discovery.osReleaseName = selectedPlatform.osReleaseName || "";
                form.discovery.osVersion = selectedPlatform.osVersion || "";
                form.discovery.machineArchitecture = selectedPlatform.machineArchitecture || "";
                form.discovery.language = selectedPlatform.language || "";
                form.discovery.locale = selectedPlatform.locale || "";

                // Update the form UI with the new values
                this._formsComponent.setPristineValue({
                    discovery: form.discovery,
                });
            } else {
                // Clear the platform fields if no platform is selected
                form.discovery.manufacturer = "";
                form.discovery.model = "";
                form.discovery.deviceType = "";
                form.discovery.osName = "";
                form.discovery.osFamily = "";
                form.discovery.osReleaseName = "";
                form.discovery.osVersion = "";
                form.discovery.machineArchitecture = "";
                form.discovery.language = "";
                form.discovery.locale = "";

                // Update the form UI with the cleared values
                this._formsComponent.setPristineValue({
                    discovery: form.discovery,
                });
            }

            this._model._device.setProperty("platformId", form.discovery.platformId || undefined);
        }

        this._model._device.setProperty(
            "manufacturer",
            form.discovery.manufacturer || defaultFalsyForProperty("manufacturer")
        );
        this._model._device.setProperty(
            "model",
            form.discovery.model || defaultFalsyForProperty("model")
        );
        this._model._device.setProperty(
            "deviceType",
            form.discovery.deviceType || defaultFalsyForProperty("deviceType")
        );
        this._model._device.setOsName(form.discovery.osName || defaultFalsyForProperty("osName"));
        this._model._device.setOsFamily(
            form.discovery.osFamily || defaultFalsyForProperty("osFamily")
        );
        this._model._device.setOsReleaseName(
            form.discovery.osReleaseName || defaultFalsyForProperty("osReleaseName")
        );
        this._model._device.setOsVersion(
            form.discovery.osVersion || defaultFalsyForProperty("osVersion")
        );
        this._model._device.setMachineArchitecture(
            form.discovery.machineArchitecture || defaultFalsyForProperty("machineArchitecture")
        );
        this._model._device.setLanguage(
            form.discovery.language || defaultFalsyForProperty("language")
        );
        this._model._device.setLocale(form.discovery.locale || defaultFalsyForProperty("locale"));
        this._model._device.setSerialNumber(
            form.discovery.serialNumber || defaultFalsyForProperty("serialNumber")
        );

        // Custom Fields
        // Updating custom fields listed in form
        _.keys(customFieldsFromForm).map((uuid) => {
            if (
                !(
                    this._customFieldHierarchyObj[uuid] &&
                    this._customFieldHierarchyObj[uuid].updateToInheritance === "inherited"
                )
            ) {
                if (
                    !this._isLooselyEqual(
                        customFieldsFromForm[uuid],
                        this._model._device.pristineProperties.customFieldValues[uuid]
                    )
                ) {
                    this._model._device.addDirectlyAssignedCustomFieldValue(
                        uuid,
                        customFieldsFromForm[uuid]
                    );
                }
            }
        });

        const updatedCustomFields = Object.assign({}, this._model._device.getCustomFieldValues());
        const updatedCfIds = Object.keys(updatedCustomFields);
        const pristineSourceCfIds = Object.keys(
            _.get(this._model._device, "pristineProperties.customFieldSources", {})
        );
        Object.keys(this._customFieldHierarchyObj).forEach((cf) => {
            if (updatedCfIds.indexOf(cf) < 0 && pristineSourceCfIds.indexOf(cf) >= 0) {
                const deviceCfSources = Object.assign({}, this._model._device.customFieldSources);
                deviceCfSources[cf] = this._model._device.pristineProperties.customFieldSources[cf];
                this._model._device.setCustomFieldSources(deviceCfSources);
                const deviceCfs = Object.assign({}, this._model._device.getCustomFieldValues());
                deviceCfs[cf] = this._model._device.pristineProperties.customFieldValues[cf];
                this._model._device.setCustomFieldValues(deviceCfs);
            }
        });

        // Extended Device Props
        _.values(this._model._extendedPropFeed.getCache()).forEach(function (extendedProp) {
            const formExtendedProp = form[extendedProp.getExtensionType()];
            if (formExtendedProp) {
                extendedProp.setEdpInstance(formExtendedProp);
            }
        });

        // this._updateAccessStatus();
    }

    async _onFormsPendingPatchesChanged() {
        if (!this._renderingForm) {
            let form = this._formsComponent.getValue();

            if (this._model._deviceCategory === "iot") {
                this._iotDevicePendingPatchesChanged(form);
                return;
            }

            let customFieldsFromForm = form.customFields ? _.cloneDeep(form.customFields) : {};
            const formName = form.general.name;
            // const customFieldsFromForm = _.pick(form, _.keys(this._model._customFieldColumnDef));

            this._model._device.setName(formName);

            let restoreCmds = _.get(form, "backupAndRestore.restoreCommands", "");
            const pristineRestoreCmds = _.get(
                this._model,
                "_device.pristineProperties.restoreCommands"
            );
            if (restoreCmds === "" && _.isEmpty(pristineRestoreCmds)) {
                this._model._device.setRestoreCommands(pristineRestoreCmds);
            } else {
                restoreCmds =
                    await this._model._CredentialsEntity.convertTemplateStringCredNamesToIds(
                        this._model._userSession.activeOrganization.id,
                        restoreCmds
                    );
                this._model._device.setRestoreCommands(restoreCmds);
            }

            if (!_.isNil(form.general.description)) {
                this._model._device.setDescription(form.general.description);
            }
            if (!_.isNil(form.general.tagIds)) {
                this._model._device.setTagIds(form.general.tagIds);
            }

            this._model._device.setManagementState(form.general.managementState);
            this._model._device.setEnvironment(form.general.environment);
            if (!this._model._isDeviceControlled) {
                this._model._device.setConnectionMethod(
                    form.connectionInformation.connectionType || "cliConnection"
                );
            }
            if (!this._model._device.discoveryLevelFrom) {
                this._model._device.setDiscoveryLevel(Number(form.discovery.discoveryLevel));
            }

            const siteId = this._model._device.getSiteId() || "/";
            if (siteId !== form.site.siteId) {
                if (_.isEmpty(form.site.siteId) || form.site.siteId === "/") {
                    this._model._device.clearSiteRelationship();
                } else {
                    this._model._device.setSiteRelationship(form.site.siteId);
                }
            } else {
                this._model._gameEditor.clearPendingRelationshipsFor(
                    "site",
                    this._model._device,
                    false
                );
            }

            this._model._device.setAllowSiteAutoAssignment(form.site.allowSiteAutoAssignment);
            this._setSiteDetails();

            if (!_.isEmpty(form.discovery.detectedTypeBase)) {
                this._model._device.detectedTypeBase = form.discovery.detectedTypeBase;
            }

            // Updating custom fields listed in form
            _.keys(customFieldsFromForm).map((uuid) => {
                if (
                    !(
                        this._customFieldHierarchyObj[uuid] &&
                        this._customFieldHierarchyObj[uuid].updateToInheritance === "inherited"
                    )
                ) {
                    if (
                        !this._isLooselyEqual(
                            customFieldsFromForm[uuid],
                            this._model._device.pristineProperties.customFieldValues[uuid]
                        )
                    ) {
                        this._model._device.addDirectlyAssignedCustomFieldValue(
                            uuid,
                            customFieldsFromForm[uuid]
                        );
                    }
                }
            });

            const updatedCustomFields = Object.assign(
                {},
                this._model._device.getCustomFieldValues()
            );
            const updatedCfIds = Object.keys(updatedCustomFields);
            const pristineSourceCfIds = Object.keys(
                _.get(this._model._device, "pristineProperties.customFieldSources", {})
            );
            Object.keys(this._customFieldHierarchyObj).forEach((cf) => {
                if (updatedCfIds.indexOf(cf) < 0 && pristineSourceCfIds.indexOf(cf) >= 0) {
                    const deviceCfSources = Object.assign(
                        {},
                        this._model._device.customFieldSources
                    );
                    deviceCfSources[cf] =
                        this._model._device.pristineProperties.customFieldSources[cf];
                    this._model._device.setCustomFieldSources(deviceCfSources);
                    const deviceCfs = Object.assign({}, this._model._device.getCustomFieldValues());
                    deviceCfs[cf] = this._model._device.pristineProperties.customFieldValues[cf];
                    this._model._device.setCustomFieldValues(deviceCfs);
                }
            });

            if (!this._model._isDeviceControlled) {
                // connectionType is removed from the form's returned object because it's in a different place in device entity
                let connectionInformation = _.omit(form.connectionInformation, ["connectionType"]);

                // The recursive function deepCopyCredsDetails finds and copies id and credsId to the form's connection object
                // Those two properties under credentials["@ref"].instance are unexpectedly added by BE after device entity save
                // This can be removed if BE stops adding these props which are not part of the JType's output
                const deepCopyCredsDetails = (pristineConnObj, path = []) => {
                    for (const key in pristineConnObj) {
                        if (pristineConnObj.hasOwnProperty(key)) {
                            if (key === "credentials") {
                                // Only copy id/credsId when credentials already exists in the form's
                                // connectionInformation. Using _.set() when the path is absent creates the
                                // credentials object from scratch, injecting invalid credentials when the
                                // user has selected an auth type that does not use credentials (e.g.
                                // switching from "userPass" to "key" on fortiManagerApiConnection).
                                if (!_.has(connectionInformation, path.concat(["credentials"]))) {
                                    continue;
                                }
                                const id = _.get(pristineConnObj, [
                                    "credentials",
                                    "@ref",
                                    "instance",
                                    "id",
                                ]);
                                if (id) {
                                    _.set(
                                        connectionInformation,
                                        path.concat(["credentials", "@ref", "instance", "id"]),
                                        id
                                    );
                                }
                                const credsId = _.get(pristineConnObj, [
                                    "credentials",
                                    "@ref",
                                    "instance",
                                    "credsId",
                                ]);
                                if (credsId) {
                                    _.set(
                                        connectionInformation,
                                        path.concat(["credentials", "@ref", "instance", "credsId"]),
                                        credsId
                                    );
                                }
                            } else if (
                                typeof pristineConnObj[key] === "object" &&
                                pristineConnObj[key] !== null
                            ) {
                                deepCopyCredsDetails(pristineConnObj[key], [...path, key]);
                            }
                        }
                    }
                };
                deepCopyCredsDetails(this._model._device.pristineProperties.connectionInformation);

                // Deep search _Site instanceName from form and save _Site(xxx) from pristineProperties on device instead
                const pathsForSiteInstanceName = this._findPathsForSiteInstanceName(
                    form.connectionInformation
                );
                pathsForSiteInstanceName.forEach((path) => {
                    const pristineInstanceName = _.get(
                        this._model._device.pristineProperties.connectionInformation,
                        path
                    );
                    if (pristineInstanceName && pristineInstanceName.startsWith("_Site")) {
                        _.set(connectionInformation, path, pristineInstanceName);
                    }
                });

                this._model._device.setConnectionInformation(connectionInformation);

                let modelEngineZoneId = this._model._device.getEngineZoneId();

                if (
                    modelEngineZoneId !== form.site.engineZoneId &&
                    !(_.isEmpty(modelEngineZoneId) && _.isEmpty(form.site.engineZoneId))
                ) {
                    if (_.isEmpty(form.site.engineZoneId)) {
                        this._model._device.clearEngineZoneRelationship();
                    } else {
                        this._model._device.setEngineZoneRelationship(form.site.engineZoneId);
                    }
                } else {
                    this._model._gameEditor.clearPendingRelationshipsFor(
                        "engineZone",
                        this._model._device,
                        false
                    );
                }

                if (!_.isEmpty(this.model.visibleEngineZoneList)) {
                    this._model._device.setLockEngine(form.site.lockEngine || false);
                }
            }

            if (form.site.siteId !== this._model._parentSite.id) {
                this._model._siteEntity
                    .loadSite(this._model._userSession.activeOrganization.id, form.site.siteId)
                    .then((site) => {
                        this._model._parentSite = site;
                        this._customFieldHierarchyObj =
                            this._siteCfUtility.getCustomFieldInheritance(
                                this._model._device,
                                this._model._parentSite,
                                this._model._customFieldColumnDef
                            );
                        this._siteCfUtility.updateCustomFieldsHierarchy(
                            this._rootElem,
                            this._model._device,
                            this._formsComponent
                        );
                        this._discoveryLevelInheritanceUtility.setParentEntity(site);
                        this._discoveryLevelInheritanceUtility.setEntity(this._model._device);
                        this._discoveryLevelInheritanceUtility.updateIconTitle();
                    });
            }

            // Commented setting haPairName from here
            // setting haPairName
            // this._model._device.setProperty('haPairName', form.haPairName); //25550 - haPair can now be patched

            this._updateAccessStatus();
            if (!this._model._isDeviceControlled) {
                this._updateHAGroup();
                if (this._model._isOSMSolutionAvailable) {
                    if (
                        this.model.getDeviceRelatedImageServerId() !== form.site.fileServerId &&
                        !(
                            _.isEmpty(this.model.getDeviceRelatedImageServerId()) &&
                            _.isEmpty(form.site.fileServerId)
                        )
                    ) {
                        if (!_.isEmpty(form.site.fileServerId)) {
                            this._model._device.setImageServerRelationship(form.site.fileServerId);
                        } else {
                            this._model._device.clearImageServerRelationship();
                        }
                    } else {
                        this._model._gameEditor.clearPendingRelationshipsFor(
                            "imageServer",
                            this._model._device,
                            false
                        );
                    }

                    if (form.osManagement.vrf !== null) {
                        this._model._device.setVrf(form.osManagement.vrf);
                    }
                    if (form.osManagement.useIssu !== null) {
                        this._model._device.setUseIssu(form.osManagement.useIssu);
                    }
                }
            }

            // EDP Update in entity
            // const edpValues = this._model._device.getEdpValues();
            // if (edpValues) {
            //     // get all EDP keys to replace in the pristine values
            //     const edpKeys = _.keys(this._model._device.getEdpValues());
            //     edpKeys.forEach((key) => {
            //         const edpEntry = form[key];
            //         Object.assign(edpValues[key], edpEntry);
            //     });

            //     this._model._device.setEdpValues(edpValues);
            // }

            _.values(this._model._extendedPropFeed.getCache()).forEach(function (extendedProp) {
                const formExtendedProp = form[extendedProp.getExtensionType()];
                if (formExtendedProp) {
                    extendedProp.setEdpInstance(formExtendedProp);
                }
            });

            this._toggleActions();
        }
    }

    _findPathsForSiteInstanceName(obj, currentPath = []) {
        let paths = [];

        if (typeof obj !== "object" || obj === null) {
            return paths;
        }

        for (const [key, value] of Object.entries(obj)) {
            const newPath = [...currentPath, key];

            if (key === "instanceName" && value === "_Site") {
                paths.push(newPath.join("."));
            }

            if (typeof value === "object") {
                paths = paths.concat(this._findPathsForSiteInstanceName(value, newPath));
            }
        }

        return paths;
    }

    findKeyValue(obj, key) {
        if (obj === null || obj === undefined) {
            return undefined;
        }

        if (Object.prototype.hasOwnProperty.call(obj, key)) {
            return obj[key];
        }

        for (const k in obj) {
            if (typeof obj[k] === "object" && obj[k] !== null) {
                const result = this.findKeyValue(obj[k], key);
                if (result !== undefined) {
                    return result;
                }
            }
        }

        // If the key is not found, return undefined
        return undefined;
    }
    findKeyPath(obj, key, path = "") {
        if (obj === null || obj === undefined) {
            return undefined;
        }

        if (Object.prototype.hasOwnProperty.call(obj, key)) {
            return path + (path ? "." : "") + key;
        }

        for (const k in obj) {
            if (typeof obj[k] === "object" && obj[k] !== null) {
                const result = this.findKeyPath(obj[k], key, path + (path ? "." : "") + k);
                if (result !== undefined) {
                    return result;
                }
            }
        }

        // If the key is not found, return undefined
        return undefined;
    }

    _onExtendedPropertiesChanged(pristineProperties, before) {
        if (!this._renderingForm) {
            let formVal = this._formsComponent.getValue(); // Data from Jtype form
            let devicePropsVal = this.getPristineValue(); // Data from _pristineProperties i.e. sync'd with BE
            let isDirty = this._isDirty;

            let updateReadOnlyFields = () => {
                _.keys(pristineProperties.edpInstance).map((id) => {
                    if (
                        !_.isUndefined(this.findKeyValue(devicePropsVal, id)) &&
                        this._model._extendedReadOnlyProps.indexOf(id) === -1
                    ) {
                        const keyPath = this.findKeyPath(devicePropsVal, id);
                        if (keyPath) {
                            _.set(devicePropsVal, keyPath, this.findKeyValue(formVal, id));
                        }
                    } else {
                        const selectedEntity =
                            _.values(this._model._extendedPropFeed.getCache()).find(
                                (extendedProp) =>
                                    extendedProp.extensionType === pristineProperties.extensionType
                            ) || {};
                        const edpInstance = selectedEntity.getEdpInstance();
                        if (
                            !_.isEqual(
                                _.get(pristineProperties, `edpInstance.${id}`),
                                _.get(selectedEntity, `edpInstance.${id}`)
                            )
                        ) {
                            edpInstance[id] = _.get(pristineProperties, `edpInstance.${id}`);
                            edpInstance.setEdpInstance(edpInstance);
                        }
                    }
                });

                this._formsComponent.setPristineValue(devicePropsVal);

                return Promise.resolve();
            };

            // Update
            if (isDirty) {
                let isReadOnlyUpdate = true;

                _.keys(pristineProperties.edpInstance).map((id) => {
                    // No need to run these checks if isReadOnlyUpdate is already false
                    if (!isReadOnlyUpdate) {
                        return;
                    }

                    if (
                        this._model._extendedReadOnlyProps.indexOf(id) === -1 &&
                        !_.isEqual(
                            _.get(pristineProperties, `edpInstance.${id}`, null),
                            _.get(before, `edpInstance.${id}`, null)
                        )
                    ) {
                        isReadOnlyUpdate = false;
                    }
                });

                if (!isReadOnlyUpdate) {
                    this._model.gameEditor.runQuickConfirmationActionWorkflow(
                        {
                            message:
                                "This device has been updated by another user. Do you want to refresh its details? All changes will be lost.",
                            title: "Update Confirmation Check",
                            confirmDetails: {
                                text: "Yes",
                                type: "text",
                            },
                        },
                        (this._onDeviceDetailsCancelAction =
                            this._onDeviceDetailsCancelAction.bind(this)),
                        updateReadOnlyFields
                    );
                } else {
                    updateReadOnlyFields();
                }
            } else {
                this._formsComponent.setPristineValue(devicePropsVal);
            }
        }
    }

    _onDevicePropertiesChanged(pristineProperties, before) {
        if (!this._renderingForm && !this._model._isEdpEntityAddedOrRemoved) {
            let formVal = this._formsComponent.getValue(); // Data from Jtype form
            let devicePropsVal = this.getPristineValue(); // Data from _pristineProperties i.e. sync'd with BE
            let isDirty = this._isDirty;

            // TODO To be removed once tables can be refreshed by form_renderer
            let refreshReadOnlyTables = () => {
                // This could've been merged into the below condition but
                // to avoid over complicating the below condition, I've added this separately
                if (this._model._deviceCategory === "iot") {
                    this._resetDeviceForm().then(() => {
                        this._model._modalUserFeedback.hideDisplayed();
                    });
                } else if (
                    !this._isLooselyEqual(
                        devicePropsVal?.components?.detectedComponents,
                        formVal?.components?.detectedComponents
                    ) ||
                    !this._isLooselyEqual(
                        devicePropsVal?.phones?.detectedPhoneModels,
                        formVal?.phones?.detectedPhoneModels
                    ) ||
                    !this._isLooselyEqual(
                        devicePropsVal?.components?.detectedChassis,
                        formVal?.components?.detectedChassis
                    ) ||
                    !this._isLooselyEqual(
                        devicePropsVal?.components?.detectedStack,
                        formVal?.components?.detectedStack
                    )
                ) {
                    this._resetDeviceForm().then(() => {
                        this._model._modalUserFeedback.hideDisplayed();
                    });
                }
            };

            let updateReadOnlyFields = () => {
                _.keys(pristineProperties).map((id) => {
                    if (
                        !_.isUndefined(this.findKeyValue(devicePropsVal, id)) &&
                        readOnlyFieldIds.indexOf(id) === -1
                    ) {
                        if (id === "customFieldValues") {
                            _.keys(pristineProperties.customFields).map((uuid) => {
                                // console.log("Processing CF '" + uuid + "' with value = '" + pristineProperties.customFieldValues[uuid] + "'");
                                if (
                                    !_.isUndefined(devicePropsVal.customFields[uuid]) &&
                                    readOnlyCustomFieldIds.indexOf(uuid) === -1
                                ) {
                                    // console.log("Not Read-Only AND Not undefined => Keeping form value '" + formVal.customFieldValues[uuid] + "'");
                                    devicePropsVal.customFields[uuid] = formVal.customFields[uuid];
                                } else {
                                    // console.log("Read-Only Or undefined => Checking if different from '" + this._model._device.customFieldValues[uuid] + "'");
                                    if (
                                        !_.isEqual(
                                            pristineProperties.customFieldValues[uuid],
                                            this._model._device.customFieldValues[uuid]
                                        )
                                    ) {
                                        // console.log("Different => Updating device root property to '" + pristineProperties.customFieldValues[uuid] + "'");
                                        this._model._device.setProperty(
                                            "customFieldValues." + uuid,
                                            pristineProperties.customFieldValues[uuid]
                                        );
                                    }
                                }
                            });
                        } else {
                            const keyPath = this.findKeyPath(devicePropsVal, id);
                            if (keyPath) {
                                _.set(devicePropsVal, keyPath, this.findKeyValue(formVal, id));
                            }
                        }
                    } else {
                        if (!_.isEqual(pristineProperties[id], this._model._device[id])) {
                            this._model._device.setProperty(id, pristineProperties[id]);
                        }
                    }
                });

                // Silently update detectedTypeBase after a discovery if the user is not trying to update it (dirty field) and the newly discovered type is different from unknown
                if (
                    devicePropsVal.discovery.detectedTypeBase ===
                        _.get(before, "detectedTypeBase", "unknown") &&
                    _.get(pristineProperties, "detectedTypeBase", "unknown") !== "unknown"
                ) {
                    devicePropsVal.discovery.detectedTypeBase = pristineProperties.detectedTypeBase;
                    this._model._device.setProperty(
                        "detectedTypeBase",
                        pristineProperties.detectedTypeBase
                    );
                }

                this._formsComponent.setPristineValue(devicePropsVal);

                if (this._model._deviceCategory !== "iot") {
                    this._updateAccessStatus();
                }

                if (
                    _.get(devicePropsVal, "osManagement.haPairName") !==
                    _.get(
                        formVal,
                        "osManagement.haPairName" && this._model._deviceCategory !== "iot"
                    )
                ) {
                    this._updateHAGroup();
                }

                refreshReadOnlyTables();

                return Promise.resolve();
            };

            if (pristineProperties.siteId !== this._model._parentSite.id) {
                this._model._siteEntity
                    .loadSite(
                        this._model._userSession.activeOrganization.id,
                        pristineProperties.siteId
                    )
                    .then((site) => {
                        this._model._parentSite = site;
                        this._customFieldHierarchyObj =
                            this._siteCfUtility.getCustomFieldInheritance(
                                this._model._device,
                                this._model._parentSite,
                                this._model._customFieldColumnDef
                            );
                        this._siteCfUtility.updateCustomFieldsHierarchy(
                            this._rootElem,
                            this._model._device,
                            this._formsComponent
                        );
                        this._discoveryLevelInheritanceUtility.setParentEntity(site);
                        this._discoveryLevelInheritanceUtility.setEntity(this._model._device);
                        this._discoveryLevelInheritanceUtility.updateIconTitle();
                    });
            }

            if (isDirty) {
                let isReadOnlyUpdate = true;

                _.keys(pristineProperties).map((id) => {
                    // No need to run these checks if isReadOnlyUpdate is already false
                    if (!isReadOnlyUpdate) {
                        return;
                    }
                    if (id === "customFieldValues") {
                        // Checking if at least one value of a non read-only custom field has been updated. If yes, isReadOnlyUpdate updated to false.
                        _.keys(pristineProperties.customFieldValues).map((uuid) => {
                            if (
                                readOnlyCustomFieldIds.indexOf(uuid) === -1 &&
                                !_.isEqual(
                                    _.get(pristineProperties, ["customFieldValues", uuid], null),
                                    _.get(before, ["customFieldValues", uuid], null)
                                )
                            ) {
                                isReadOnlyUpdate = false;
                            }
                        });
                        // Checking if at least one non read-only custom field has been added and removed. If yes, isReadOnlyUpdate updated to false.
                        if (isReadOnlyUpdate) {
                            const diff = _.xor(
                                _.isEmpty(pristineProperties.customFieldValues)
                                    ? []
                                    : Object.keys(pristineProperties.customFieldValues),
                                _.isEmpty(before.customFieldValues)
                                    ? []
                                    : Object.keys(before.customFieldValues)
                            );

                            if (!diff.every((uuid) => readOnlyCustomFieldIds.includes(uuid))) {
                                isReadOnlyUpdate = false;
                            }
                        }
                    } else if (id === "detectedTypeBase") {
                        const newDetectedType = _.get(pristineProperties, id, "unknown");
                        const prevDetectedType = _.get(before, id, "unknown");
                        if (
                            !_.isEqual(newDetectedType, prevDetectedType) &&
                            prevDetectedType !== "unknown"
                        ) {
                            isReadOnlyUpdate = false;
                        }
                    } else {
                        if (
                            readOnlyFieldIds.indexOf(id) === -1 &&
                            !_.isEqual(_.get(pristineProperties, id, null), _.get(before, id, null))
                        ) {
                            isReadOnlyUpdate = false;
                        }
                    }
                });

                if (!isReadOnlyUpdate) {
                    this._model.gameEditor.runQuickConfirmationActionWorkflow(
                        {
                            message:
                                "This device has been updated by another user. Do you want to refresh its details? All changes will be lost.",
                            title: "Update Confirmation Check",
                            confirmDetails: {
                                text: "Yes",
                                type: "text",
                            },
                        },
                        (this._onDeviceDetailsCancelAction =
                            this._onDeviceDetailsCancelAction.bind(this)),
                        updateReadOnlyFields
                    );
                } else {
                    updateReadOnlyFields();
                }
            } else {
                this._formsComponent.setPristineValue(devicePropsVal);
                if (this._model._deviceCategory !== "iot") {
                    this._updateAccessStatus();
                    this._updateHAGroup();
                }
                refreshReadOnlyTables();
            }
        }
    }

    _onDirty(dirtyState) {
        // console.log(this._model._device.pendingPatches);
        this._isDirty = dirtyState;
        this._toggleActions();
    }

    _onDeviceDetailsSave() {
        this._disableViewActions();
        this._model.removeListener("DevicePropertiesChanged", this._onDevicePropertiesChanged);
        return this._model
            .saveEdp()
            .then(() => this._model.saveDevice())
            .then(() => {
                return this.model.resetDeviceProperties();
            })
            .then(() => {
                this._model.on(
                    "DevicePropertiesChanged",
                    (this._onDevicePropertiesChanged = this._onDevicePropertiesChanged.bind(this))
                );
                return this._enableViewActions();
            })
            .then(() => {
                return this._model._siteEntity.loadSite(
                    this._model._userSession.activeOrganization.id,
                    this._formsComponent.getValue().site.siteId
                );
            })
            .then((site) => {
                this._model._parentSite = site;
                this._customFieldHierarchyObj = this._siteCfUtility.getCustomFieldInheritance(
                    this._model._device,
                    this._model._parentSite,
                    this._model._customFieldColumnDef
                );
                this._siteCfUtility.updateIconTitles(this._rootElem);

                if (this._model._deviceCategory !== "iot") {
                    this._discoveryLevelInheritanceUtility.setParentEntity(site);
                    this._discoveryLevelInheritanceUtility.setEntity(this._model._device);
                    this._discoveryLevelInheritanceUtility.updateIconTitle();
                }
            })
            .catch((/*err*/) => {
                this._enableViewActions();
                this._toggleActions();
            });
    }

    _onDeviceDetailsCancel() {
        this._model.gameEditor.runQuickConfirmationActionWorkflow(
            {
                message: "Are you sure you want to cancel? All changes will be lost.",
                title: "Cancel Confirmation Check",
                confirmDetails: {
                    text: "Yes",
                    type: "text",
                },
            },
            (this._onDeviceDetailsCancelAction = this._onDeviceDetailsCancelAction.bind(this))
        );
    }

    async _onDeviceDetailsCancelAction() {
        await this._model.resetDeviceProperties();
        this._resetDeviceForm();
    }

    _onOsImageServerFeedUpdated() {
        return this._resetDeviceForm();
    }

    _onSiteFeedUpdated() {
        const formPathVal = this._formsComponent.getValue().site.siteId;
        const inList = this.model.siteIds.indexOf(formPathVal);
        if (inList < 0) {
            this._resetPathPatch();
        }
        return this._resetDeviceForm.then(() => {
            this._discoveryLevelInheritanceUtility.setSiteNameMap(this._model._siteNamemap);
            return Promise.resolve();
        });
    }

    _resetPathPatch() {
        const patchArr = this._formsComponent.getPendingPatches();
        const pathPatchIndex = patchArr.findIndex((patch) => {
            return patch.path === "/siteId";
        });
        patchArr.splice(pathPatchIndex, 1);
        this._formsComponent._setPendingPatches(patchArr);
    }

    _onEngineZoneFeedUpdated() {
        if (!this._isDirty) {
            return this._resetDeviceForm();
        }
    }

    _onOrgZoneFeedUpdated() {
        if (!this._isDirty) {
            return this._resetDeviceForm();
        }
    }

    _onOdmValueMapFeedUpdated() {
        if (!this._isDirty) {
            return this._resetDeviceForm();
        }
    }

    _onViewHAClick() {
        let haGroupDevice = new haGroupComponent(this._model);
        haGroupDevice.renderer.render().then(() => {
            this._childViewRenderer = new childViewComponent({
                data: haGroupDevice.renderer.rootElement,
                backLink: "Device Details",
                title: "HA Group",
                childData: haGroupDevice,
                parentData: this._model,
                backButton: true,
            });
        });
    }
    _onRemoveHAClick() {
        this.model.gameEditor.runQuickConfirmationActionWorkflow(
            {
                message: "Are you sure you want to Remove HA Device?",
                title: "Remove HA Pair",
                confirmDetails: {
                    text: "Yes",
                    type: "destructive",
                },
            },
            (this._onHADeviceRemoveAction = this._onHADeviceRemoveAction.bind(this))
        );
    }
    _onHADeviceRemoveAction() {
        return Promise.resolve().then(() => {
            this._model._device.clearHaPairRelationship();
            /* 25550 -- start */
            this._model._device.setProperty("haPairName", null);
            // let devPatches = this._model._device.getPendingPatches();
            // if(devPatches && devPatches.length === 1 && devPatches[0].path === "/haPairName" && this._model._device.getHaPairName() === null) {
            //     this._model._device.clearPendingPatches();
            // }

            // this._model.updateRelationships();
            /* 25550 -- end */
            this._updateHAGroup(true);
            // case 25550
            this._formsComponent.updatePendingPatches([
                {
                    op: "replace",
                    path: "/haPairName",
                    value: null,
                },
            ]);
        });
    }
    _onCustomFieldUpdated() {
        this._renderDeviceDetails();
    }

    // Event to check if edp types have changed
    async _edpAddedOrRemoved() {
        this._model._unregisterAllListeners();
        await this._model._getExtendedPropTypes();
        this._unregisterEditorGroupsEvents();
        await this.render(
            "Extended Property Types condition has been changed. Device details refreshing."
        );
        this._model._isEdpEntityAddedOrRemoved = false;
    }

    _hideLeftFooterContainer() {
        let leftSelector = document.querySelector("left-container");
        leftSelector.style.display = "none";
    }
    _showLeftFooterContainer() {
        let querySelector = document.querySelector("left-container");
        querySelector.style.display = "block";
    }

    // case 25550
    _updateHAPairFormVal() {
        const haPairName = this._model._device.getHaPairName();
        const formPatch = this._formsComponent.getPendingPatches();
        const haPatch =
            formPatch &&
            formPatch.filter((patch) => {
                if (patch.path.search("haPairName") >= 0) {
                    return true;
                }
                return false;
            });

        if (haPatch && haPatch.length > 0 && haPatch[0].value === haPairName) {
            return;
        }
        // formPatch.forEach(patch => {
        //     if(patch.path.search("haPairName" >= 0)) {
        //         patch.value = haPairName;
        //     }
        // });
        // this._formsComponent._setPendingPatches(formPatch);
        this._formsComponent.updatePendingPatches([
            {op: "replace", path: "/haPairName", value: haPairName},
        ]);
        this._formsComponent.renderer._rootElem.querySelector(
            ".ha_pair_name input-text-control input"
        ).value = haPairName;
        // const formVals = this._formsComponent.getPristineValue();
        // formVals.haPairName = haPairName;
        // this._formsComponent.setPristineValue(formVals);
        // console.log(this._formsComponent, this._formsComponent.getPendingPatches());
        let pristineHaName = this._model._device.pristineProperties.haPairName;
        if (pristineHaName === undefined) {
            pristineHaName = null;
        }
        if (pristineHaName === haPairName) {
            this._model._device.clearPendingPatches();
            this._model._gameEditor.clearPendingRelationshipsFor("haPair", this._model._device);
        }
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE API METHODS               */
    /*******************************************/
    _hideConnectionMethodDropDown() {
        let dropDownContainerSelector =
            "device-details-view-gl gl-form-renderer div.connectionInformation gl-form-renderer div.select_options div.select_options_header gl-multi-select div.chosen-container select";
        let dropDownContainer =
            this._formsComponent.renderer._rootElem.querySelector(dropDownContainerSelector);

        if (this._model._device.connectionMethodLocked && dropDownContainer) {
            dropDownContainer.classList.add("disabled");
        }
    }

    _setSiteDetails() {
        const formsComponent = this._deviceEditorGroups._renderer.getFormsComponent();
        const pathFieldVal =
            formsComponent.renderer.rootElement.querySelector(".site_id select")?.selectedOptions[0]
                ?.text;
        if (!pathFieldVal) {
            this._logger.warn("no path value");
            return;
        }
        this._pathFieldValue = pathFieldVal;
        const siteCodeField =
            formsComponent.renderer.rootElement.querySelector(".site_code_name input");
        // const siteTypeField = this._formsComponent.renderer.rootElement.querySelector(".site_location_type input");
        const pathValue = pathFieldVal;
        // console.log(pathFieldVal, pathValue, this._model._siteOptionsMap);
        siteCodeField.value = _.get(this._model._siteOptionsMap[pathValue], "code", "");
        // const siteValue = _.get(this._model._siteOptionsMap[pathValue], "type", "");
        // siteTypeField.value = this._model._siteEntity.getLocationTypeLabels()[siteValue] || "";
    }

    _flatten(obj, roots = [], sep = " ") {
        // find props of given object
        return (
            Object.keys(obj)
                // return an object by iterating props
                .reduce(
                    (memo, prop) =>
                        Object.assign(
                            // create a new object
                            {},
                            // include previously returned object
                            memo,
                            Object.prototype.toString.call(obj[prop]) === "[object Object]"
                                ? // keep working if value is an object
                                  this._flatten(obj[prop], roots.concat([prop]))
                                : // include current prop and value and prefix prop with the roots
                                  {[roots.concat([prop]).join(sep)]: obj[prop] + ""} //stringify the value here for boolean and other edge cases
                        ),
                    {}
                )
        );
    }

    _toLocaleString(val) {
        let d = new Date(val);
        if (Object.prototype.toString.call(d) === "[object Date]" && !isNaN(d.getTime())) {
            return d.toLocaleDateString() + " " + d.toLocaleTimeString();
        } else {
            return "";
        }
    }

    getIotPristineValue() {
        let pristineValue = {};

        if (this.model._device) {
            // General
            pristineValue.general = {};
            pristineValue.general.name = _.get(this._model._device, "name");
            pristineValue.general.description = _.get(this._model._device, "description");
            pristineValue.general.tagIds = _.get(this._model._device, "tagIds") || [];
            pristineValue.general.environment = _.get(this._model._device, "environment");
            pristineValue.general.licenseCount = `${_.get(
                this._model._device,
                "licenseCount",
                ""
            )}`;
            pristineValue.general.managementState = _.get(this._model._device, "managementState");

            // Site
            pristineValue.site = {};
            pristineValue.site.siteId = _.get(this._model._device, "siteId", "/");
            pristineValue.site.siteCodeName = _.get(this._model._device, "siteCodeName");
            pristineValue.site.allowSiteAutoAssignment = _.get(
                this._model._device,
                "allowSiteAutoAssignment",
                true
            );

            // Discovery
            pristineValue.discovery = {};
            pristineValue.discovery.offlineDiscoveryStatus = _.get(
                this._model._device,
                "offlineDiscoveryStatus"
            );
            pristineValue.discovery.platformId = _.get(this._model._device, "platformId");
            pristineValue.discovery.detectedTypeBase = _.get(
                this._model._device,
                "detectedTypeBase"
            );
            pristineValue.discovery.manufacturer = _.get(this._model._device, "manufacturer");
            pristineValue.discovery.model = _.get(this._model._device, "model");
            pristineValue.discovery.deviceType = _.get(this._model._device, "deviceType");
            pristineValue.discovery.osName = _.get(this._model._device, "osName");
            pristineValue.discovery.osFamily = _.get(this._model._device, "osFamily");
            pristineValue.discovery.osReleaseName = _.get(this._model._device, "osReleaseName");
            pristineValue.discovery.osVersion = _.get(this._model._device, "osVersion");
            pristineValue.discovery.machineArchitecture = _.get(
                this._model._device,
                "machineArchitecture"
            );
            pristineValue.discovery.language = _.get(this._model._device, "language");
            pristineValue.discovery.locale = _.get(this._model._device, "locale");
            pristineValue.discovery.serialNumber = _.get(this._model._device, "serialNumber");

            // Custom Fields
            pristineValue.customFields = _.get(this._model._device, "customFieldValues");

            // OS Management
            pristineValue.osManagement = {};

            pristineValue.osManagement.offlineUpgradedOn = this._toLocaleString(
                _.get(this._model._device, "offlineUpgradedOn")
            );
            pristineValue.osManagement.upgradeUploadedBy = _.get(
                this._model._device,
                "upgradeUploadedBy",
                ""
            );
            pristineValue.osManagement.upgradeUploadedOn = this._toLocaleString(
                _.get(this._model._device, "upgradeUploadedOn", "")
            );
            pristineValue.osManagement.offlineUpgradeError = _.get(
                this._model._device,
                "offlineUpgradeError"
            );

            // Activity
            pristineValue.activity = {};
            pristineValue.activity.lastActivityBy = _.get(this._model._device, "lastActivityBy");
            pristineValue.activity.lastActivityError = _.get(
                this._model._device,
                "lastActivityError",
                ""
            );
            pristineValue.activity.lastActivityOn = this._toLocaleString(
                _.get(this._model._device, "lastActivityOn")
            );
            pristineValue.activity.lastActivityType = _.get(
                this._model._device,
                "lastActivityType",
                ""
            );
            pristineValue.activity.offlineDiscoveredOn = this._toLocaleString(
                _.get(this._model._device, "offlineDiscoveredOn")
            );
            pristineValue.activity.discoveryUploadedBy = _.get(
                this._model._device,
                "discoveryUploadedBy"
            );

            pristineValue.activity.discoveryUploadedOn = this._toLocaleString(
                _.get(this._model._device, "discoveryUploadedOn", "")
            );
            pristineValue.activity.offlineDiscoveryError = _.get(
                this._model._device,
                "offlineDiscoveryError"
            );
            pristineValue.activity.updatedBy = this._model._device.updatedBy;
            pristineValue.activity.updatedOn = this._toLocaleString(this._model._device.updatedOn);

            // CMDB
            pristineValue.cmdb = {};
            pristineValue.cmdb.cmdbCiId = _.get(this._model._device, "cmdbCiId");
            pristineValue.cmdb.cmdbCiName = _.get(this._model._device, "cmdbCiName");
            pristineValue.cmdb.cmdbClassName = _.get(this._model._device, "cmdbClassName");
            pristineValue.cmdb.cmdbSyncStatus = _.get(this._model._device, "cmdbSyncStatus");
            pristineValue.cmdb.lastCmdbSyncBy = _.get(this._model._device, "lastCmdbSyncBy");
            pristineValue.cmdb.lastCmdbSyncError = _.get(this._model._device, "lastCmdbSyncError");
            pristineValue.cmdb.lastCmdbSyncOn = this._toLocaleString(
                _.get(this._model._device, "lastCmdbSyncOn")
            );

            // Extended Device Properties
            _.values(this._model._extendedPropFeed.getCache()).forEach(function (extendedProp) {
                Object.assign(pristineValue, {
                    [extendedProp.getExtensionType()]: extendedProp.getEdpInstance(),
                });
            });
        }

        return pristineValue;
    }

    getPristineValue() {
        if (this._model._deviceCategory === "iot") {
            return this.getIotPristineValue();
        }

        let pristineProperties = this.model._device.getPristineProperties(),
            pristineValue = {};

        if (this.model._device) {
            // General
            pristineValue.general = {};
            pristineValue.general.name = _.get(this._model._device, "name");
            pristineValue.general.description = _.get(this._model._device, "description");
            pristineValue.general.tagIds = _.get(this._model._device, "tagIds") || [];
            pristineValue.general.environment = _.get(this._model._device, "environment");
            pristineValue.general.licenseCount = `${_.get(
                this._model._device,
                "licenseCount",
                ""
            )}`;
            pristineValue.general.managementState = _.get(this._model._device, "managementState");

            // Site
            pristineValue.site = {};
            pristineValue.site.siteId = _.get(this._model._device, "siteId", "/");
            pristineValue.site.siteCodeName = _.get(this._model._device, "siteCodeName");
            pristineValue.site.lockEngine = _.get(this._model._device, "lockEngine", false);
            pristineValue.site.fileServerId = this.model.getDeviceRelatedImageServerId() || null;
            pristineValue.site.engineZoneId = this.model._device.getEngineZoneId() || null;
            pristineValue.site.allowSiteAutoAssignment = _.get(
                this._model._device,
                "allowSiteAutoAssignment",
                true
            );

            // Discovery
            pristineValue.discovery = {};
            pristineValue.discovery.detectedHostName = _.get(
                this._model._device,
                "detectedHostName"
            );
            pristineValue.discovery.detectedOs = _.get(this._model._device, "detectedOs");
            pristineValue.discovery.detectedSerialNumber = _.get(
                this._model._device,
                "detectedSerialNumber"
            );
            pristineValue.discovery.detectedSku = `${_.get(
                this._model._device,
                "detectedSku",
                ""
            )}`;
            pristineValue.discovery.detectedSoftwareVersion = _.get(
                this._model._device,
                "detectedSoftwareVersion"
            );
            pristineValue.discovery.detectedTypeBase = _.get(
                this._model._device,
                "detectedTypeBase"
            );
            pristineValue.discovery.detectedUptime = _.get(this._model._device, "detectedUptime")
                ? moment
                      .duration(_.get(this._model._device, "detectedUptime"), "milliseconds")
                      .format("d:hh:mm:ss", {trim: false})
                : "";
            pristineValue.discovery.detectedVendor = `${_.get(
                this._model._device,
                "detectedVendor"
            )}`;

            pristineValue.discovery.discoveryLevel = _.get(this._model._device, "discoveryLevel");
            pristineValue.discovery.discoveryStatus = _.get(this._model._device, "discoveryStatus");
            pristineValue.discovery.elementCount = _.get(this._model._device, "elementCount", "");

            // Custom Fields
            pristineValue.customFields = _.get(this._model._device, "customFieldValues");

            // Controller
            const controlType = _.get(this._model._device, "controlType");
            if (controlType && controlType !== "NA") {
                pristineValue.controller = {};
                pristineValue.controller.controlType = _.get(
                    this._model._DeviceEntity.getControlTypeLabels(),
                    controlType,
                    "N/A"
                );
                pristineValue.controller.controllerName = _.get(
                    this._model._device,
                    "controllerName"
                );
            }

            // Configuration Drift
            pristineValue.configurationDrift = {};
            pristineValue.configurationDrift.latestHaveIChanged = _.get(
                this._model._device,
                "latestHaveIChanged"
            );
            pristineValue.configurationDrift.latestHaveIChangedLastChecked = this._toLocaleString(
                _.get(this._model._device, "latestHaveIChangedLastChecked")
            );
            pristineValue.configurationDrift.capturedBy = _.get(this._model._device, "capturedBy");
            pristineValue.configurationDrift.configuredBy = _.get(
                this._model._device,
                "configuredBy"
            );
            pristineValue.configurationDrift.configuredInfo = _.get(
                this._model._device,
                "configuredInfo",
                ""
            );
            pristineValue.configurationDrift.configuredOn = this._toLocaleString(
                _.get(this._model._device, "configuredOn")
            );
            pristineValue.configurationDrift.configuredType = _.get(
                this._model._device,
                "configuredType"
            );

            // Configuration Audit
            pristineValue.configurationAudit = {};
            pristineValue.configurationAudit.auditStatus = _.get(
                this._model._device,
                "auditStatus",
                ""
            );
            pristineValue.configurationAudit.auditedBy = _.get(
                this._model._device,
                "auditedBy",
                ""
            );
            pristineValue.configurationAudit.auditedOn = this._toLocaleString(
                _.get(this._model._device, "auditedOn", "")
            );

            pristineValue.configurationAudit.lastAuditPolicy = _.get(
                this._model._device,
                "lastAuditPolicy"
            );

            // Backup&Restore
            pristineValue.backupAndRestore = {};
            pristineValue.backupAndRestore.lastBackupStatus = _.get(
                this._model._device,
                "lastBackupStatus",
                ""
            );
            pristineValue.backupAndRestore.lastBackupOn = this._toLocaleString(
                _.get(this._model._device, "lastBackupOn")
            );
            pristineValue.backupAndRestore.lastBackedUpBy = _.get(
                this._model._device,
                "lastBackedUpBy"
            );
            pristineValue.backupAndRestore.lastSuccessfulBackupName = _.get(
                this._model._device,
                "lastSuccessfulBackupName"
            );
            pristineValue.backupAndRestore.lastSuccessfulBackupOn = this._toLocaleString(
                _.get(this._model._device, "lastSuccessfulBackupOn", "")
            );
            pristineValue.backupAndRestore.lastRestoredStatus = _.get(
                this._model._device,
                "lastRestoredStatus",
                ""
            );
            pristineValue.backupAndRestore.lastRestoredProgress = _.get(
                this._model._device,
                "lastRestoredProgress"
            );
            pristineValue.backupAndRestore.lastRestoredOn = this._toLocaleString(
                _.get(this._model._device, "lastRestoredOn")
            );
            pristineValue.backupAndRestore.lastRestoredBy = _.get(
                this._model._device,
                "lastRestoredBy",
                ""
            );
            pristineValue.backupAndRestore.lastRestoredBackupName = _.get(
                this._model._device,
                "lastRestoredBackupName"
            );
            pristineValue.backupAndRestore.lastRestoredBackupFrom = _.get(
                this._model._device,
                "lastRestoredBackupFrom"
            );
            pristineValue.backupAndRestore.restoreCommands = _.get(
                this._model._device,
                "restoreCommands",
                ""
            );

            // OS Management
            pristineValue.osManagement = {};
            pristineValue.osManagement.haPairName = _.get(this._model._device, "haPairName", "");
            pristineValue.osManagement.osUpgradeError = _.get(
                this._model._device,
                "osUpgradeError",
                ""
            );
            pristineValue.osManagement.osUpgradePlanAction = _.get(
                this._model._device,
                "osUpgradePlanAction",
                ""
            );
            pristineValue.osManagement.osUpgradePlanName = _.get(
                this._model._device,
                "osUpgradePlanName"
            );
            pristineValue.osManagement.osUpgradeStatus = this._getOsUpgradeTitle(
                _.get(this._model._device, "osUpgradeStatus", "PENDING")
            );
            pristineValue.osManagement.osUpgradedBy = _.get(
                this._model._device,
                "osUpgradedBy",
                ""
            );
            pristineValue.osManagement.osUpgradedOn = this._toLocaleString(
                _.get(this._model._device, "osUpgradedOn")
            );
            pristineValue.osManagement.useIssu = _.get(this._model._device, "useIssu", "");
            pristineValue.osManagement.vrf = _.get(this._model._device, "vrf", "");

            // Config Modeling
            if (!_.isEmpty(_.get(this._model._device, "nodeProps"))) {
                pristineValue.configModeling = _.get(this._model._device, "nodeProps");
            }

            // Activity
            pristineValue.activity = {};
            pristineValue.activity.lastActivityBy = _.get(this._model._device, "lastActivityBy");
            pristineValue.activity.lastActivityError = _.get(
                this._model._device,
                "lastActivityError",
                ""
            );
            pristineValue.activity.lastActivityOn = this._toLocaleString(
                _.get(this._model._device, "lastActivityOn")
            );
            pristineValue.activity.lastActivityType = _.get(
                this._model._device,
                "lastActivityType",
                ""
            );
            pristineValue.discovery.discoveredBy = _.get(this._model._device, "discoveredBy");
            pristineValue.discovery.discoveredOn = this._toLocaleString(
                _.get(this._model._device, "discoveredOn")
            );
            pristineValue.activity.rebootedBy = _.get(this._model._device, "rebootedBy");
            pristineValue.activity.rebootedOn = this._toLocaleString(
                _.get(this._model._device, "rebootedOn", "")
            );
            pristineValue.activity.updatedBy = this._model._device.updatedBy;
            pristineValue.activity.updatedOn = this._toLocaleString(this._model._device.updatedOn);

            // CMDB
            pristineValue.cmdb = {};
            pristineValue.cmdb.cmdbCiId = _.get(this._model._device, "cmdbCiId");
            pristineValue.cmdb.cmdbCiName = _.get(this._model._device, "cmdbCiName");
            pristineValue.cmdb.cmdbClassName = _.get(this._model._device, "cmdbClassName");
            pristineValue.cmdb.cmdbSyncStatus = _.get(this._model._device, "cmdbSyncStatus");
            pristineValue.cmdb.lastCmdbSyncBy = _.get(this._model._device, "lastCmdbSyncBy");
            pristineValue.cmdb.lastCmdbSyncError = _.get(this._model._device, "lastCmdbSyncError");
            pristineValue.cmdb.lastCmdbSyncOn = this._toLocaleString(
                _.get(this._model._device, "lastCmdbSyncOn")
            );

            // DETECTED CHASSIS
            pristineValue.components = {};
            pristineValue.components.freeCount = `${_.get(
                this._model._device,
                "portSummary.freeCount",
                ""
            )}`;
            pristineValue.components.freePercent = `${_.get(
                this._model._device,
                "portSummary.freePercent",
                ""
            )}`;
            pristineValue.components.totalCount = `${_.get(
                this._model._device,
                "portSummary.totalCount",
                ""
            )}`;
            pristineValue.components.useCount = `${_.get(
                this._model._device,
                "portSummary.useCount",
                ""
            )}`;
            pristineValue.components.usePercent = `${_.get(
                this._model._device,
                "portSummary.usePercent",
                ""
            )}`;

            if (_.get(pristineProperties, "detectedChassis")) {
                const chassis = _.get(pristineProperties, "detectedChassis");
                pristineValue.components.detectedChassis = [];

                for (const property in chassis) {
                    let flatProperty = this._flatten(chassis[property]);
                    let id = {id: property}; //chassis name is always labeled "ID" for now
                    pristineValue.components.detectedChassis.push(
                        Object.assign({}, id, flatProperty)
                    );
                }
            }

            // DETECTED STACK
            if (_.get(pristineProperties, "detectedStack")) {
                const stack = _.get(pristineProperties, "detectedStack");
                let universalStackProperties = {};
                pristineValue.components.detectedStack = [];

                for (const property in stack) {
                    if (typeof stack[property] === "object") {
                        let flatProperty = this._flatten(stack[property]);
                        let id = {id: property}; //stack name is always labeled "ID" for now
                        pristineValue.components.detectedStack.push(
                            Object.assign({}, id, flatProperty)
                        );
                    }
                    // any non-object becomes a universal key: value pair that will get added to all stack objects
                    // except stack name and stack id
                    else {
                        if (property === "stackId") {
                            pristineValue.components.detectedStackId = stack[property];
                        } else if (property === "stackName") {
                            pristineValue.components.detectedStackName = stack[property];
                        } else {
                            universalStackProperties[property] = stack[property];
                        }
                    }
                }

                //integrate and detected universal properties back into the stack objects
                if (!_.isEmpty(universalStackProperties)) {
                    pristineValue.components.detectedStack.forEach((stackItem, i) => {
                        Object.keys(universalStackProperties).forEach((newItem) => {
                            pristineValue.components.detectedStack[i][newItem] =
                                universalStackProperties[newItem];
                        });
                    });
                }
            }

            if (_.get(pristineValue.components, "detectedClusterMembers")) {
                const clusters = _.get(this._model._device, "detectedClusterMembers", {});
                pristineValue.components.containerCluster = {};
                pristineValue.components.containerCluster.detectedClusters = Object.keys(
                    clusters
                ).reduce((clusterAcc, clusterId) => {
                    clusterAcc.push(
                        Object.keys(clusterColumns).reduce((columnAcc, columnId) => {
                            //special exception for "_id" (name) value not being pathable
                            if (columnId === "_id") {
                                columnAcc[columnId] = clusterId;
                                //special exception for "uptime" value is an improper string
                            } else if (columnId === "uptime") {
                                const regex = /s.*ms$/g;
                                columnAcc[columnId] = clusters[clusterId][columnId].replace(
                                    regex,
                                    "s"
                                );
                            } else if (clusterColumns[columnId].path) {
                                columnAcc[columnId] = _.get(
                                    clusters[clusterId],
                                    clusterColumns[columnId].path
                                );
                            } else {
                                columnAcc[columnId] = clusters[clusterId][columnId];
                            }
                            return columnAcc;
                        }, {})
                    );
                    return clusterAcc;
                }, []);
            }

            // DETECTED COMPONENTS
            if (_.get(pristineProperties, "detectedComponents")) {
                pristineValue.components.detectedComponents = _.mapValues(
                    _.get(pristineProperties, "detectedComponents"),
                    function (modules) {
                        modules = _.map(modules, (module) => {
                            if (_.isObject(module)) {
                                module.type = _.get(module, "location.type", "").toString();
                                module.path = _.get(module, "location.path", "").toString();
                                delete module.location;
                            }
                            return module;
                        });
                        return modules;
                    }
                );
            }

            // DETECTED PHONE MODELS
            if (_.get(pristineProperties, "detectedPhoneModels")) {
                const phoneModelsObject = _.get(pristineProperties, "detectedPhoneModels");
                pristineValue.phones = pristineValue.phones || {};
                pristineValue.phones.detectedPhoneModels = Object.entries(phoneModelsObject).map(
                    ([modelName, modelData]) => ({
                        model: modelName,
                        pid: modelData.pid || "",
                        hwGeneration: modelData.hwGeneration || "",
                        count: modelData.count || 0,
                    })
                );
            }

            // Access Status
            pristineValue.accessStatus = {};
            pristineValue.accessStatus.accessStatus = this._getTitle(
                _.get(this._model._device, "accessStatus", "UNKNOWN")
            );
            pristineValue.accessStatus.accessStatusText = this._getTitle(
                _.get(this._model._device, "accessStatus", "UNKNOWN")
            );

            // CONNECTION METHODS
            pristineValue.connectionInformation = {};
            pristineValue.connectionInformation.connectionType =
                pristineProperties.connectionMethod || "cliConnection";
            _.merge(pristineValue.connectionInformation, pristineProperties.connectionInformation);

            // Extended Device Properties
            _.values(this._model._extendedPropFeed.getCache()).forEach(function (extendedProp) {
                Object.assign(pristineValue, {
                    [extendedProp.getExtensionType()]: extendedProp.getEdpInstance(),
                });
            });
        }

        return pristineValue;
    }

    _getIotEntitySchemaObject() {
        const entitySchema = {};

        // entitySchema.accessStatus = {};
        // entitySchema.accessStatus["props"] = {
        //     accessStatus: {
        //         value: "",
        //         label: "",
        //         dataType: "string",
        //     },
        //     accessStatusText: {
        //         value: "",
        //         label: "Access Status",
        //         dataType: "string",
        //         readOnly: true,
        //     },
        // };

        // GENERAL
        entitySchema.general = {};
        entitySchema.general["label"] = "General";

        entitySchema.general["props"] = {
            name: {
                value: "",
                label: "Name",
                dataType: "string",
            },
            description: {
                value: "",
                label: "Description",
                dataType: "string",
            },
            // Multi-select chips via array_component.control_adapter.js (@Display.tagList).
            tagIds: {
                value: {
                    tagIds: this.model.tagOptions.map((tag) => _.keys(tag)[0]),
                    "tagIds@Display.label": "Tags",
                    "tagIds@Display.tagList.labels": this.model.tagOptions.map(
                        (tag) => _.values(tag)[0]
                    ),
                    // New option ids are prefixed "__new__" (see MultipleComboBoxControlComponent.createOption).
                    "tagIds@Display.tagList.createOption": this.model._canCreateTagsOnTheFly,
                },
                label: "Tags",
                dataType: "jType",
            },
            managementState: {
                value: "",
                label: "Management State",
                dataType: "enum",
                options: [
                    {MANAGED: "Managed"},
                    {UNMANAGED: "Unmanaged"},
                    {INVENTORY_ONLY: "Inventory Only"},
                ],
            },
            environment: {
                value: "",
                label: "Environment",
                dataType: "enum",
                options: [
                    {PRODUCTION: "Production"},
                    {TEST: "Test"},
                    {"PRODUCTION-TEST": "Production/Test"},
                ],
            },
            licenseCount: {
                value: "",
                label: "License Count",
                dataType: "string",
                readOnly: true,
            },
        };

        // SITE
        entitySchema.site = {};
        entitySchema.site["label"] = "Site";

        entitySchema.site["props"] = {
            siteCodeName: {
                value: "",
                label: "Site Code Name",
                dataType: "string",
                readOnly: true,
            },
            siteId: {
                value: "",
                label: "Site Path",
                dataType: "enum",
                options: this._getSiteOptions(),
            },
            allowSiteAutoAssignment: {
                value: false,
                label: "Allow site auto-assignment",
                dataType: "boolean",
            },
        };

        // DISCOVERY
        entitySchema.discovery = {};
        entitySchema.discovery["label"] = "Discovery";

        const typeBaseVals = Object.values(
            Object.fromEntries(
                Object.entries(this._model._device.getTypeBaseMap()).filter(
                    ([key]) => this._model._device.getTypeBaseToCategoryMap()[key] === "iot"
                )
            )
        )
            .filter((val) => val !== "Unknown")
            .sort();

        entitySchema.discovery["props"] = {
            offlineDiscoveryStatus: {
                value: "",
                label: "Offline Discovered Status",
                dataType: "string",
                readOnly: true,
            },
            detectedTypeBase: {
                value: "",
                label: "Type",
                dataType: "enum",
                required: true,
                options: [].concat("unknown", typeBaseVals).map((id, idx) => {
                    return {
                        [id]: [].concat("Unknown", typeBaseVals)[idx],
                    };
                }),
            },
            platformId: {
                value: "",
                label: "Platform Name",
                dataType: "enum",
                options: this._model._odmOsPlatformsMap || [],
            },
            manufacturer: {
                value: "",
                label: "Manufacturer",
                dataType: "enum",
                options: this._model._manufacturerOptions || [],
            },
            deviceType: {
                value: "",
                label: "IoT Device Type",
                dataType: "enum",
                options: this._model._deviceTypeOptions || [],
            },
            model: {
                value: "",
                label: "Model",
                dataType: "enum",
                options: this._model._modelOptions || [],
            },
            osFamily: {
                value: "",
                label: "OS Family",
                dataType: "enum",
                options: OS_FAMILY_OPTIONS,
            },
            osName: {
                value: "",
                label: "OS Name",
                dataType: "string",
            },
            osReleaseName: {
                value: "",
                label: "OS Release Name",
                dataType: "string",
            },
            osVersion: {
                value: "",
                label: "OS Version",
                dataType: "string",
            },
            machineArchitecture: {
                value: "",
                label: "Machine Architecture",
                dataType: "string",
            },
            language: {
                value: "",
                label: "OS Language",
                dataType: "string",
            },
            locale: {
                value: "",
                label: "Locale",
                dataType: "string",
            },
            serialNumber: {
                value: "",
                label: "Serial",
                dataType: "string",
            },
            macAddresses: {
                value: "",
                label: "MAC addresses",
                dataType: "string",
                readOnly: true,
            },
        };

        // CUSTOM FIELDS
        entitySchema.customFields = {};
        entitySchema.customFields["label"] = "Custom Fields";

        let customFields = this._model._device.customFieldValues;
        const customFieldValues = this.model.customFieldsJtypes;
        const customFieldsJType = {};
        if (customFields && customFieldValues && customFieldValues.length > 0) {
            Object.assign(customFieldsJType, ...customFieldValues);
            Object.keys(_.get(this._model._device, "customFieldValues")).forEach((key) => {
                if (key in customFieldsJType) {
                    customFieldsJType[key] = _.get(this._model._device, "customFieldValues")[key];
                }
            });
        }

        entitySchema.customFields["jtype"] = customFieldsJType;

        if (_.isEmpty(customFields) || _.isEmpty(customFieldValues)) {
            delete entitySchema.customFields;
        }

        // OS Management
        entitySchema.osManagement = {};
        entitySchema.osManagement["label"] = "OS Management";

        entitySchema.osManagement["props"] = {
            offlineUpgradedOn: {
                value: "",
                label: "Last Upgraded On",
                dataType: "dateTime",
                readOnly: true,
            },
            upgradeUploadedBy: {
                value: "",
                label: "Upgrade Results Loaded By",
                dataType: "string",
                readOnly: true,
            },
            upgradeUploadedOn: {
                value: "",
                label: "Upgrade Results Loaded On",
                dataType: "dateTime",
                readOnly: true,
            },
            offlineUpgradeError: {
                value: "",
                label: "Last Upgrade Error",
                dataType: "string",
                readOnly: true,
            },
        };

        // EDP
        this._model._extendedPropTypes.forEach((extendedProp) => {
            const extensionType = extendedProp.getExtensionType();
            entitySchema[extensionType] = {};
            entitySchema[extensionType]["label"] = extendedProp.getDisplayName();

            const extendedPropTypeJType = extendedProp.getJtype();

            entitySchema[extensionType]["props"] = {
                edpProps: {
                    value: extendedPropTypeJType,
                    label: "EDP",
                    dataType: "jType",
                },
            };
        });

        // ACTIVITY
        entitySchema.activity = {};
        entitySchema.activity["label"] = "Activity";

        entitySchema.activity["props"] = {
            lastActivityType: {
                value: _.get(this._model._device, "lastActivityType"),
                label: "Last Activity Type",
                dataType: "string",
                readOnly: true,
            },
            lastActivityOn: {
                value: _.get(this._model._device, "lastActivityOn"),
                label: "Last Activity On",
                dataType: "dateTime",
                readOnly: true,
            },
            lastActivityBy: {
                value: _.get(this._model._device, "lastActivityBy"),
                label: "Last Activity By",
                dataType: "string",
                readOnly: true,
            },
            lastActivityError: {
                value: _.get(this._model._device, "lastActivityError"),
                label: "Last Activity Error",
                dataType: "string",
                readOnly: true,
            },
            offlineDiscoveredOn: {
                value: _.get(this._model._device, "offlineDiscoveredOn"),
                label: "Offline Discovered On",
                dataType: "dateTime",
                readOnly: true,
            },
            offlineDiscoveryError: {
                value: "",
                label: "Offline Discovery Error",
                dataType: "string",
                readOnly: true,
            },
            discoveryUploadedBy: {
                value: "",
                label: "Discovery Uploaded By",
                dataType: "string",
                readOnly: true,
            },
            discoveryUploadedOn: {
                value: "",
                label: "Discovery Uploaded On",
                dataType: "dateTime",
                readOnly: true,
            },
            updatedOn: {
                value: "",
                label: "Updated On",
                dataType: "dateTime",
                readOnly: true,
            },
            updatedBy: {
                value: "",
                label: "Updated By",
                dataType: "string",
                readOnly: true,
            },
        };

        // CMDB Details
        entitySchema.cmdb = {};
        entitySchema.cmdb["label"] = "CMDB Details";

        entitySchema.cmdb["props"] = {
            cmdbCiId: {
                value: _.get(this._model._device, "cmdbCiId"),
                label: "CMDB CI ID",
                dataType: "string",
                readOnly: true,
            },
            cmdbCiName: {
                value: _.get(this._model._device, "cmdbCiName"),
                label: "CMDB CI Name",
                dataType: "string",
                readOnly: true,
            },
            cmdbClassName: {
                value: _.get(this._model._device, "cmdbClassName"),
                label: "CMDB Class Name",
                dataType: "string",
                readOnly: true,
            },
            cmdbSyncStatus: {
                value: _.get(this._model._device, "cmdbSyncStatus"),
                label: "CMDB Sync Status",
                dataType: "string",
                readOnly: true,
            },
            lastCmdbSyncBy: {
                value: _.get(this._model._device, "lastCmdbSyncBy"),
                label: "Last CMDB Sync By",
                dataType: "string",
                readOnly: true,
            },
            lastCmdbSyncError: {
                value: _.get(this._model._device, "lastCmdbSyncError"),
                label: "Last CMDB Sync Error",
                dataType: "string",
                readOnly: true,
            },
            lastCmdbSyncOn: {
                value: _.get(this._model._device, "lastCmdbSyncOn"),
                label: "Last CMDB Sync On",
                dataType: "dateTime",
                readOnly: true,
            },
        };

        return entitySchema;
    }

    _getSiteOptions() {
        // siteIds/sitePaths are getters that rebuild the whole list on each access; read once.
        const siteIds = this.model.siteIds;
        const sitePaths = this.model.sitePaths;
        return siteIds.map((id, idx) => ({[id]: sitePaths[idx]}));
    }

    _getEntitySchemaObject() {
        const entitySchema = {};

        if (this._model._deviceCategory === "iot") {
            return this._getIotEntitySchemaObject();
        }

        entitySchema.accessStatus = {};
        entitySchema.accessStatus["props"] = {
            accessStatus: {
                value: "",
                label: "",
                dataType: "string",
            },
            accessStatusText: {
                value: "",
                label: "Access Status",
                dataType: "string",
                readOnly: true,
            },
        };

        entitySchema.general = {};
        entitySchema.general["label"] = "General";

        entitySchema.general["props"] = {
            name: {
                value: "",
                label: "Name",
                dataType: "string",
            },
            description: {
                value: "",
                label: "Description",
                dataType: "string",
            },
            // Multi-select chips via array_component.control_adapter.js (@Display.tagList).
            tagIds: {
                value: {
                    tagIds: this.model.tagOptions.map((tag) => _.keys(tag)[0]),
                    "tagIds@Display.label": "Tags",
                    "tagIds@Display.tagList.labels": this.model.tagOptions.map(
                        (tag) => _.values(tag)[0]
                    ),
                    "tagIds@Display.tagList.minLineCount": 5,
                    // New option ids are prefixed "__new__" (see MultipleComboBoxControlComponent.createOption).
                    "tagIds@Display.tagList.createOption": this.model._canCreateTagsOnTheFly,
                },
                label: "Tags",
                dataType: "jType",
            },
            managementState: {
                value: "",
                label: "Management State",
                dataType: "enum",
                options: [
                    {MANAGED: "Managed"},
                    {UNMANAGED: "Unmanaged"},
                    {INVENTORY_ONLY: "Inventory Only"},
                ],
            },
            environment: {
                value: "",
                label: "Environment",
                dataType: "enum",
                options: [
                    {PRODUCTION: "Production"},
                    {TEST: "Test"},
                    {"PRODUCTION-TEST": "Production/Test"},
                ],
            },
            licenseCount: {
                value: "",
                label: "License Count",
                dataType: "string",
                readOnly: true,
            },
        };

        // TODO : Is the device check needed ???
        if (this._model._device && !this._model._isDeviceControlled) {
            entitySchema.connectionInformation = {};
            entitySchema.connectionInformation["label"] = "Connection";
            entitySchema.connectionInformation["jtype"] = {};
            entitySchema.connectionInformation["typeDef"] = {};
            const connectionObjectValue = {
                connectionObject: {
                    "connectionType@Display.label": "Connection Method",
                    "connectionType@Required": true,
                },
            };

            Object.assign(
                connectionObjectValue.connectionObject,
                this._model._connectionDropdownList
            );
            Object.assign(
                entitySchema.connectionInformation["typeDef"],
                this._model._connectionTypeDefs
            );

            connectionObjectValue.connectionObject["connectionType"] = _.get(
                this._model._device,
                "connectionMethod"
            );

            _.merge(
                entitySchema.connectionInformation["jtype"],
                connectionObjectValue.connectionObject
            );
        }

        // SITE
        entitySchema.site = {};
        entitySchema.site["label"] = "Site";

        entitySchema.site["props"] = {
            siteCodeName: {
                value: "",
                label: "Site Code Name",
                dataType: "string",
                readOnly: true,
            },
            siteId: {
                value: "",
                label: "Site Path",
                dataType: "enum",
                options: this._getSiteOptions(),
            },
            allowSiteAutoAssignment: {
                value: false,
                label: "Allow site auto-assignment",
                dataType: "boolean",
            },
            engineZoneId: {
                value: "",
                label: "Zone",
                dataType: "enum",
                options: this.model.engineZoneIds.map((id, idx) => {
                    return {
                        [id]: this.model.zoneNames[idx],
                    };
                }),
            },
            lockEngine: {
                value: false,
                label: "Lock Zone",
                dataType: "boolean",
            },
            fileServerId: {
                value: "",
                label: "File Server",
                dataType: "enum",
                options: this.model.osImageServerIds.map((id, idx) => {
                    return {
                        [id]: this.model.osImageServerNames[idx],
                    };
                }),
            },
        };

        // DISCOVERY
        entitySchema.discovery = {};
        entitySchema.discovery["label"] = "Discovery";

        const discoveryLevelValues = Object.values(
            this._model._DeviceEntity.getDiscoveryLevelValues()
        ).map(Number); //must convert to number, Object.keys won't auto convert either

        const discoveryLevelLabels = Object.values(
            this._model._DeviceEntity.getDiscoveryLevelLabels()
        );

        const typeBaseVals = Object.values(
            Object.fromEntries(
                Object.entries(this._model._device.getTypeBaseMap()).filter(
                    ([key]) => this._model._device.getTypeBaseToCategoryMap()[key] === "network"
                )
            )
        )
            .filter((val) => val !== "Unknown")
            .sort();

        entitySchema.discovery["props"] = {
            discoveryLevel: {
                value: "",
                label: "Discovery Level",
                dataType: "enum",
                required: true,
                options: discoveryLevelValues.map((id, idx) => {
                    return {
                        [id]: discoveryLevelLabels[idx],
                    };
                }),
            },
            discoveryStatus: {
                value: "",
                label: "Discovered Status",
                dataType: "string",
                readOnly: true,
            },
            detectedVendor: {
                value: "",
                label: "Vendor",
                dataType: "string",
                readOnly: true,
            },
            detectedTypeBase: {
                value: "",
                label: "Type",
                dataType: "enum",
                required: true,
                options: [].concat("unknown", typeBaseVals).map((id, idx) => {
                    return {
                        [id]: [].concat("Unknown", typeBaseVals)[idx],
                    };
                }),
            },
            detectedHostName: {
                value: "",
                label: "Hostname",
                dataType: "string",
                readOnly: true,
            },
            detectedOs: {
                value: "",
                label: "OS",
                dataType: "string",
                readOnly: true,
            },
            detectedSoftwareVersion: {
                value: "",
                label: "OS Version",
                dataType: "string",
                readOnly: true,
            },
            detectedSerialNumber: {
                value: "",
                label: "Serial",
                dataType: "string",
                readOnly: true,
            },
            detectedSku: {
                value: "",
                label: "SKUs",
                dataType: "string",
                readOnly: true,
            },
            detectedUptime: {
                value: "",
                label: "Up Time",
                dataType: "string",
                readOnly: true,
            },
            elementCount: {
                value: "",
                label: "Elements",
                dataType: "string",
                readOnly: true,
            },
        };

        // CONTROLLER
        const controlType = _.get(this._model._device, "controlType");
        if (controlType && controlType !== "NA") {
            entitySchema.controller = {};
            entitySchema.controller["label"] = "Controller";
            entitySchema.controller["props"] = {
                controlType: {
                    value: controlType,
                    label: "Controller Type",
                    dataType: "string",
                    readOnly: true,
                },
                controllerName: {
                    value: _.get(this._model._device, "controllerName"),
                    label: "Controller Name",
                    dataType: "string",
                    readOnly: true,
                },
            };
        }

        // CUSTOM FIELDS
        entitySchema.customFields = {};
        entitySchema.customFields["label"] = "Custom Fields";

        let customFields = this._model._device.customFieldValues;
        const customFieldValues = this.model.customFieldsJtypes;
        const customFieldsJType = {};
        if (customFields && customFieldValues && customFieldValues.length > 0) {
            Object.assign(customFieldsJType, ...customFieldValues);
            Object.keys(_.get(this._model._device, "customFieldValues")).forEach((key) => {
                if (key in customFieldsJType) {
                    customFieldsJType[key] = _.get(this._model._device, "customFieldValues")[key];
                }
            });
        }

        entitySchema.customFields["jtype"] = customFieldsJType;

        if (_.isEmpty(customFields) || _.isEmpty(customFieldValues)) {
            delete entitySchema.customFields;
        }

        // COMPONENTS
        const pristineValue = this.getPristineValue();

        let detectedChassisJtype = {};
        if (
            _.get(pristineValue, "components.detectedChassis") &&
            _.get(pristineValue, "components.detectedChassis").length
        ) {
            let minLineCount = Math.min(pristineValue.components.detectedChassis.length, 5);
            let columns = {};
            let detectedChassis = {};
            for (const entry of pristineValue.components.detectedChassis) {
                for (const property in entry) {
                    switch (property) {
                        case "eoxBulletinNumber":
                        case "endOfSale":
                        case "endOfMaintenance":
                        case "endOfSupport":
                            break;
                        case "id":
                            columns["id"] = {
                                label: "ID",
                                sort: "asc",
                                showSort: true,
                            };
                            break;
                        case "sku":
                        case "SKU":
                        case "Sku":
                            columns["sku"] = {
                                label: "SKU",
                                sort: "asc",
                                showSort: true,
                            };
                            break;
                        default:
                            columns[property] = {
                                label: this._model._DeviceEntity.propNameToLabel(property),
                                showSort: true,
                            };
                    }
                }
            }
            for (const property in columns) {
                detectedChassis[property] = "";
                detectedChassis[property + "@Display.description"] =
                    this._model._DeviceEntity.propNameToLabel(property);
            }

            detectedChassisJtype.detectedChassis = [];
            detectedChassisJtype["detectedChassis@Display.label"] = "Chassis";
            detectedChassisJtype["detectedChassis@Display.readOnly"] = true;
            detectedChassisJtype["detectedChassis@Display.table"] = {
                showLineNumbers: false,
                showReorder: false,
                allowEdit: false,
                showAdd: false,
                showDelete: false,
                showDetailForm: false,
                forceFitColumns: true,
                minLineCount: minLineCount,
                columns: columns,
            };
            detectedChassisJtype.detectedChassis = [detectedChassis];
        }

        let detectedStackJtype = {};
        if (
            _.get(pristineValue, "components.detectedStack") &&
            _.get(pristineValue, "components.detectedStack").length
        ) {
            let minLineCount = Math.min(pristineValue.components.detectedStack.length, 5);
            let columns = {};
            let detectedStack = {};
            for (const entry of pristineValue.components.detectedStack) {
                for (const property in entry) {
                    switch (property) {
                        case "eoxBulletinNumber":
                        case "endOfSale":
                        case "endOfMaintenance":
                        case "endOfSupport":
                            break;
                        case "id":
                            columns["id"] = {
                                label: "ID",
                                sort: "asc",
                                showSort: true,
                            };
                            break;
                        case "sku":
                        case "SKU":
                        case "Sku":
                            columns["sku"] = {
                                label: "SKU",
                                showSort: true,
                            };
                            break;
                        default:
                            columns[property] = {
                                label: this._model._DeviceEntity.propNameToLabel(property),
                                showSort: true,
                            };
                    }
                }
            }
            for (const property in columns) {
                detectedStack[property] = "";
                detectedStack[property + "@Display.description"] =
                    this._model._DeviceEntity.propNameToLabel(property);
            }

            //some stacks have names and IDs (Meraki)
            if (_.get(pristineValue, "components.detectedStackName")) {
                detectedStackJtype.detectedStackName = "";
                detectedStackJtype["detectedStackName@Display.label"] = "Stack Name";
            }
            if (_.get(pristineValue, "components.detectedStackId")) {
                detectedStackJtype.detectedStackId = "";
                detectedStackJtype["detectedStackId@Display.label"] = "Stack ID";
                detectedStackJtype["detectedStackId@Display.readOnly"] = "Stack ID";
            }

            detectedStackJtype.detectedStack = [];
            detectedStackJtype["detectedStack@Display.label"] = "Stack";
            detectedStackJtype["detectedStack@fieldType"] = "Object";
            detectedStackJtype["detectedStack@Display.readOnly"] = true;
            detectedStackJtype["detectedStack@Display.table"] = {
                showLineNumbers: false,
                showReorder: false,
                allowEdit: false,
                showAdd: false,
                showDelete: false,
                showDetailForm: false,
                forceFitColumns: true,
                minLineCount: minLineCount,
                columns: columns,
            };
            detectedStackJtype.detectedStack = [detectedStack];
            detectedStackJtype["detectedStack@propertyOrder"] = ["id", "sku", "version"];

            detectedStackJtype.lineBreak13 = null;
            detectedStackJtype["lineBreak13@Display.label"] = "&nbsp;";
        }

        // DETECTED CLUSTERS
        let detectedClusterJtype = {};
        if (
            _.get(pristineValue, "components.containerCluster.detectedClusters") &&
            _.get(pristineValue, "components.containerCluster.detectedClusters").length
        ) {
            let minLineCount = Math.min(
                pristineValue.components.containerCluster.detectedClusters.length,
                5
            );
            let detectedClusters = {};

            const clusterColsArray = Object.keys(clusterColumns);
            clusterColsArray.forEach((property) => {
                detectedClusters[property] = "";
                detectedClusters[property + "@Display.description"] =
                    this._model._DeviceEntity.propNameToLabel(property);
            });

            detectedClusterJtype.containerCluster = {};
            detectedClusterJtype.containerCluster.detectedClusters = [];
            detectedClusterJtype["containerCluster@Display.label"] = "Clusters";
            detectedClusterJtype["containerCluster@Display.accordion"] = {};
            detectedClusterJtype["containerCluster@fieldType"] = "Object";
            detectedClusterJtype.containerCluster["detectedClusters@Display.readOnly"] = true;
            detectedClusterJtype.containerCluster["detectedClusters@Display.label"] = "";
            detectedClusterJtype.containerCluster["detectedClusters@Display.table"] = {
                showLineNumbers: false,
                showReorder: false,
                allowEdit: false,
                showAdd: false,
                showDelete: false,
                showDetailForm: false,
                forceFitColumns: true,
                minLineCount: minLineCount,
                columns: clusterColumns,
            };

            detectedClusterJtype.containerCluster.detectedClusters = [detectedClusters];

            detectedClusterJtype.lineBreak14 = null;
            detectedClusterJtype["lineBreak14@Display.label"] = "&nbsp;";
        }

        const detectedComponentsJtype = {};
        const compObject = _.get(pristineValue, "components.detectedComponents");
        if (compObject && Object.keys(compObject).length !== 0) {
            detectedComponentsJtype.detectedComponents = {};
            detectedComponentsJtype["detectedComponents@Display.label"] = "Components";
            for (let [key, value] of Object.entries(
                pristineValue.components.detectedComponents || {}
            )) {
                if (_.isArray(value) && value.length > 0) {
                    let minLineCount = Math.min(value.length, 5);
                    let label = value[0].label || value[0].type;
                    detectedComponentsJtype.detectedComponents[key + "@Display.label"] = label;
                    detectedComponentsJtype.detectedComponents[key + "@Display.readOnly"] = true;
                    detectedComponentsJtype.detectedComponents[key + "@Display.table"] = {
                        showLineNumbers: false,
                        showReorder: false,
                        minLineCount: minLineCount,
                        allowEdit: false,
                        showAdd: false,
                        showDelete: false,
                        showDetailForm: false,
                        forceFitColumns: true,
                        columns: {
                            description: {
                                label: "Description",
                                showSort: true,
                            },
                            sku: {
                                label: "SKU",
                                sort: "asc",
                                showSort: true,
                            },
                            serialNumber: {
                                label: "Serial",
                                showSort: true,
                            },
                            path: {
                                label: "Path",
                                showSort: true,
                            },
                            type: {
                                label: "Type",
                                showSort: true,
                            },
                        },
                    };

                    detectedComponentsJtype.detectedComponents[key] = [
                        {
                            description: "",
                            "description@Display.description": "Description",
                            sku: "",
                            "sku@Display.description": "SKU",
                            serialNumber: "",
                            "serialNumber@Display.description": "Serial",
                            path: "",
                            "path@Display.description": "Path",
                            type: "",
                            "type@Display.description": "Type",
                        },
                    ];
                }
            }
        }

        const detectedPhoneModelsJtype = {};
        const phoneModelArray = _.get(pristineValue, "phones.detectedPhoneModels");
        if (phoneModelArray && phoneModelArray.length) {
            let minLineCount = Math.min(phoneModelArray.length, 5);
            let phoneModelTemplate = {
                model: "",
                "model@Display.description": "Model",
                pid: "",
                "pid@Display.description": "Reference PID",
                hwGeneration: "",
                "hwGeneration@Display.description": "HW Generation",
                count: 0,
                "count@Display.description": "Count",
            };

            detectedPhoneModelsJtype.detectedPhoneModels = [];
            detectedPhoneModelsJtype["detectedPhoneModels@Display.label"] = "Models";
            detectedPhoneModelsJtype["detectedPhoneModels@Display.readOnly"] = true;
            detectedPhoneModelsJtype["detectedPhoneModels@Display.table"] = {
                showLineNumbers: false,
                showReorder: false,
                minLineCount: minLineCount,
                allowEdit: false,
                showAdd: false,
                showDelete: false,
                showDetailForm: false,
                forceFitColumns: true,
                columns: {
                    model: {
                        label: "Model",
                        sort: "asc",
                        showSort: true,
                    },
                    pid: {
                        label: "Reference PID",
                        showSort: true,
                    },
                    hwGeneration: {
                        label: "HW Generation",
                        showSort: true,
                    },
                    count: {
                        label: "Count",
                        showSort: true,
                    },
                },
            };
            detectedPhoneModelsJtype.detectedPhoneModels = [phoneModelTemplate];
        }

        entitySchema.components = {};
        entitySchema.components["label"] = "Components";
        entitySchema.components["props"] = {
            totalCount: {
                value: "",
                label: "Switchports Total",
                dataType: "number",
                readOnly: true,
            },
            useCount: {
                value: "",
                label: "Switchports Used",
                dataType: "number",
                readOnly: true,
            },
            freeCount: {
                value: "",
                label: "Switchports Free",
                dataType: "number",
                readOnly: true,
            },
            usePercent: {
                value: "",
                label: "Switchports Used %",
                dataType: "number",
                readOnly: true,
            },
            freePercent: {
                value: "",
                label: "Switchports Free %",
                dataType: "number",
                readOnly: true,
            },
            detectedChassisField: {
                value: detectedChassisJtype,
                label: "Chassis",
                dataType: "jType",
                readOnly: true,
            },
            detectedStackField: {
                value: detectedStackJtype,
                label: "Detected Stack",
                dataType: "jType",
                readOnly: true,
            },
            detectedComponentsField: {
                value: detectedComponentsJtype,
                label: "Detected Components",
                dataType: "jType",
                readOnly: true,
            },
            detectedClustersField: {
                value: detectedClusterJtype,
                label: "Detected Clusters",
                dataType: "jType",
                readOnly: true,
            },
        };

        // Phones - only show if there are detected phone models
        if (phoneModelArray && phoneModelArray.length > 0) {
            entitySchema.phones = {};
            entitySchema.phones["label"] = "Phones";
            entitySchema.phones["props"] = {
                detectedPhoneModelsField: {
                    value: detectedPhoneModelsJtype,
                    label: "Models",
                    dataType: "jType",
                    readOnly: true,
                },
            };
        }

        // Configuration Drift
        entitySchema.configurationDrift = {};
        entitySchema.configurationDrift["label"] = "Configuration Drift";
        entitySchema.configurationDrift["props"] = {
            latestHaveIChanged: {
                value: _.get(this._model._device, "latestHaveIChanged"),
                label: "Captured Status",
                dataType: "string",
                readOnly: true,
            },
            latestHaveIChangedLastChecked: {
                value: _.get(this._model._device, "latestHaveIChangedLastChecked"),
                label: "Captured On",
                dataType: "dateTime",
                readOnly: true,
            },
            capturedBy: {
                value: _.get(this._model._device, "capturedBy"),
                label: "Captured By",
                dataType: "string",
                readOnly: true,
            },
            configuredInfo: {
                value: "",
                label: "Configure Info",
                dataType: "string",
                readOnly: true,
            },
            configuredType: {
                value: "",
                label: "Configure Type",
                dataType: "string",
                readOnly: true,
            },
            configuredOn: {
                value: "",
                label: "Configured On",
                dataType: "dateTime",
                readOnly: true,
            },
            configuredBy: {
                value: "",
                label: "Configured By",
                dataType: "string",
                readOnly: true,
            },
        };

        // Configuration Audit
        entitySchema.configurationAudit = {};
        entitySchema.configurationAudit["label"] = "Configuration Audit";

        entitySchema.configurationAudit["props"] = {
            auditStatus: {
                value: _.get(this._model._device, "auditStatus"),
                label: "Audit Status",
                dataType: "string",
                readOnly: true,
            },
            lastAuditPolicy: {
                value: _.get(this._model._device, "lastAuditPolicy"),
                label: "Audit Policy",
                dataType: "string",
                readOnly: true,
            },
            auditedBy: {
                value: _.get(this._model._device, "auditedBy"),
                label: "Audited By",
                dataType: "string",
                readOnly: true,
            },
            auditedOn: {
                value: _.get(this._model._device, "auditedOn"),
                label: "Audited On",
                dataType: "dateTime",
                readOnly: true,
            },
        };

        // Backup and Restore
        entitySchema.backupAndRestore = {};
        entitySchema.backupAndRestore["label"] = "Backup and Restore";

        entitySchema.backupAndRestore["props"] = {
            lastBackupStatus: {
                value: _.get(this._model._device, "lastBackupStatus"),
                label: "Backup Status",
                dataType: "string",
                readOnly: true,
            },
            lastBackupOn: {
                value: _.get(this._model._device, "lastBackupOn"),
                label: "Backup On",
                dataType: "dateTime",
                readOnly: true,
            },
            lastBackedUpBy: {
                value: _.get(this._model._device, "lastBackedUpBy"),
                label: "Backup By",
                dataType: "string",
                readOnly: true,
            },
            lastSuccessfulBackupName: {
                value: _.get(this._model._device, "lastSuccessfulBackupName"),
                label: "Last Successful Backup Name",
                dataType: "string",
                readOnly: true,
            },
            lastSuccessfulBackupOn: {
                value: _.get(this._model._device, "lastSuccessfulBackupOn"),
                label: "Last Successful Backup On",
                dataType: "dateTime",
                readOnly: true,
            },
            lastRestoredStatus: {
                value: _.get(this._model._device, "lastRestoredStatus"),
                label: "Restore Status",
                dataType: "string",
                readOnly: true,
            },
            lastRestoredProgress: {
                value: _.get(this._model._device, "lastRestoredProgress"),
                label: "Restore Progress",
                dataType: "string",
                readOnly: true,
            },
            lastRestoredOn: {
                value: _.get(this._model._device, "lastRestoredOn"),
                label: "Restore On",
                dataType: "dateTime",
                readOnly: true,
            },
            lastRestoredBy: {
                value: _.get(this._model._device, "lastRestoredBy"),
                label: "Restore By",
                dataType: "dateTime",
                readOnly: true,
            },
            lastRestoredBackupName: {
                value: _.get(this._model._device, "lastRestoredBackupName"),
                label: "Restored Backup Name",
                dataType: "string",
                readOnly: true,
            },
            lastRestoredBackupFrom: {
                value: _.get(this._model._device, "lastRestoredBackupFrom"),
                label: "Restored Backup From",
                dataType: "string",
                readOnly: true,
            },
            restoreCommands: {
                value: _.get(this._model._device, "restoreCommands"),
                label: "Post Restore Commands",
                dataType: "text",
                readOnly: false,
            },
        };

        // OS Management
        entitySchema.osManagement = {};
        entitySchema.osManagement["label"] = "OS Management";

        entitySchema.osManagement["props"] = {
            vrf: {
                value: "",
                label: "VRF",
                dataType: "string",
            },
            haPairName: {
                value: "",
                label: "HA Group",
                dataType: "string",
                readOnly: true,
            },
            useIssu: {
                value: false,
                label: "Non-disruptive upgrades",
                dataType: "boolean",
            },
            osUpgradeStatus: {
                value: "",
                label: "OSM Action Status",
                dataType: "string",
                readOnly: true,
            },
            osUpgradedOn: {
                value: "",
                label: "OSM Action On",
                dataType: "dateTime",
                readOnly: true,
            },
            osUpgradedBy: {
                value: "",
                label: "OSM Action By",
                dataType: "string",
                readOnly: true,
            },
            osUpgradePlanName: {
                value: "",
                label: "OS Plan Name",
                dataType: "string",
                readOnly: true,
            },
            osUpgradePlanAction: {
                value: "",
                label: "OS Plan Action",
                dataType: "string",
                readOnly: true,
            },
            osUpgradeError: {
                value: "",
                label: "OSM Action Error",
                dataType: "string",
                readOnly: true,
            },
        };

        // CONFIG MODELING
        entitySchema.configModeling = {};
        entitySchema.configModeling["label"] = "Config Modeling";

        const nodePropsJType = {};
        Object.entries(_.get(this._model._device, "nodeProps", {})).map(([key, value]) => {
            Object.assign(nodePropsJType, {
                [key]: value,
                [`${key}@Display.label`]: _.startCase(key),
                [`${key}@Display.readOnly`]: true,
            });
        });

        entitySchema.configModeling["props"] = {
            nodeProps: {
                value: nodePropsJType,
                label: "Node Props",
                dataType: "jType",
            },
        };
        if (_.isEmpty(_.get(this._model._device, "nodeProps"))) {
            delete entitySchema.configModeling;
        }

        // EDP
        this._model._extendedPropTypes.forEach((extendedProp) => {
            const extensionType = extendedProp.getExtensionType();
            entitySchema[extensionType] = {};
            entitySchema[extensionType]["label"] = extendedProp.getDisplayName();

            const extendedPropTypeJType = extendedProp.getJtype();

            entitySchema[extensionType]["props"] = {
                edpProps: {
                    value: extendedPropTypeJType,
                    label: "EDP",
                    dataType: "jType",
                },
            };
        });

        // ACTIVITY
        entitySchema.activity = {};
        entitySchema.activity["label"] = "Activity";

        entitySchema.activity["props"] = {
            lastActivityType: {
                value: _.get(this._model._device, "lastActivityType"),
                label: "Last Activity Type",
                dataType: "string",
                readOnly: true,
            },
            lastActivityOn: {
                value: _.get(this._model._device, "lastActivityOn"),
                label: "Last Activity On",
                dataType: "dateTime",
                readOnly: true,
            },
            lastActivityBy: {
                value: _.get(this._model._device, "lastActivityBy"),
                label: "Last Activity By",
                dataType: "string",
                readOnly: true,
            },
            lastActivityError: {
                value: _.get(this._model._device, "lastActivityError"),
                label: "Last Activity Error",
                dataType: "string",
                readOnly: true,
            },
            discoveredOn: {
                value: _.get(this._model._device, "discoveredOn"),
                label: "Discovered On",
                dataType: "dateTime",
                readOnly: true,
            },
            discoveredBy: {
                value: _.get(this._model._device, "discoveredBy"),
                label: "Discovered By",
                dataType: "string",
                readOnly: true,
            },
            rebootedOn: {
                value: "",
                label: "Rebooted On",
                dataType: "dateTime",
                readOnly: true,
            },
            rebootedBy: {
                value: "",
                label: "Rebooted By",
                dataType: "string",
                readOnly: true,
            },
            updatedOn: {
                value: "",
                label: "Updated On",
                dataType: "dateTime",
                readOnly: true,
            },
            updatedBy: {
                value: "",
                label: "Updated By",
                dataType: "string",
                readOnly: true,
            },
        };

        // CMDB Details
        entitySchema.cmdb = {};
        entitySchema.cmdb["label"] = "CMDB Details";

        entitySchema.cmdb["props"] = {
            cmdbCiId: {
                value: _.get(this._model._device, "cmdbCiId"),
                label: "CMDB CI ID",
                dataType: "string",
                readOnly: true,
            },
            cmdbCiName: {
                value: _.get(this._model._device, "cmdbCiName"),
                label: "CMDB CI Name",
                dataType: "string",
                readOnly: true,
            },
            cmdbClassName: {
                value: _.get(this._model._device, "cmdbClassName"),
                label: "CMDB Class Name",
                dataType: "string",
                readOnly: true,
            },
            cmdbSyncStatus: {
                value: _.get(this._model._device, "cmdbSyncStatus"),
                label: "CMDB Sync Status",
                dataType: "string",
                readOnly: true,
            },
            lastCmdbSyncBy: {
                value: _.get(this._model._device, "lastCmdbSyncBy"),
                label: "Last CMDB Sync By",
                dataType: "string",
                readOnly: true,
            },
            lastCmdbSyncError: {
                value: _.get(this._model._device, "lastCmdbSyncError"),
                label: "Last CMDB Sync Error",
                dataType: "string",
                readOnly: true,
            },
            lastCmdbSyncOn: {
                value: _.get(this._model._device, "lastCmdbSyncOn"),
                label: "Last CMDB Sync On",
                dataType: "dateTime",
                readOnly: true,
            },
        };

        return entitySchema;
    }

    _updateHAGroup(removed = false) {
        if (!this.model._isOSMSolutionAvailable || this._model._isDeviceControlled) {
            return;
        }

        const haPairName = this.model._device.getHaPairName();
        const formsComponent = this._deviceEditorGroups._renderer.getFormsComponent();
        const element = formsComponent.renderer._rootElem.querySelector(
            ".ha_pair_name input-text-control"
        );
        const elementInput = formsComponent.renderer._rootElem.querySelector(
            ".ha_pair_name input-text-control input"
        );

        if (!element || !elementInput) {
            console.warn(
                "DeviceDetailsViewRenderer._updateHAGroup: HA group update aborted. Required DOM elements not found."
            );
            return;
        }

        element.appendChild(this._viewHAContainer);

        if (!haPairName || removed) {
            this._setViewActionText("Add");
            elementInput.classList.add("hidden");
            this._removeHAButton.hide();
        } else {
            this._setViewActionText("Update");
            elementInput.classList.remove("hidden");
            this._removeHAButton.show();
        }
    }

    _updateAccessStatus() {
        const formsComponent = this._deviceEditorGroups._renderer.getFormsComponent();
        const accessStatusElem = formsComponent.renderer._rootElem.querySelector(
                ".rendered_control.access_status"
            ),
            accessStatus = this._model._device.getAccessStatus() || "UNKNOWN";

        if (accessStatusElem) {
            accessStatusElem.classList.add(accessStatus.toLowerCase());
            accessStatusElem.innerHTML = "";

            const iconSection = document.createElement("div");
            iconSection.classList.add("icon-container");

            const titleSection = document.createElement("div");
            titleSection.classList.add("title-container");

            const statusIcon = document.createElement("base-icon-gl");
            statusIcon.dataset.icon = `access.${accessStatus.toLowerCase()}`;

            iconSection.appendChild(statusIcon);

            titleSection.innerText = this._getTitle(accessStatus);

            accessStatusElem.appendChild(iconSection);
            accessStatusElem.appendChild(titleSection);
        }
    }

    _getTitle(status) {
        const statuses = this._model._device.accessStatusLabels;
        return statuses[status] || "UNKNOWN";
    }

    _getOsUpgradeTitle(status) {
        const statuses = this._model._device.getOsUpgradeStatusLabels();
        return statuses[status] || "Pending";
    }

    _setViewActionText(buttonText) {
        let buttonControl = _.get(this._viewHAContainer.querySelectorAll("button-control"), 0);

        if (buttonControl) {
            buttonControl.classList.remove("dark");
            buttonControl.innerHTML = buttonText;
        }
    }
    _refreshGrid() {
        // Case 25550
        this._updateHAGroup();
        return Promise.resolve();
    }

    _resetDeviceForm() {
        const schemaObject = this._getEntitySchemaObject();
        const pristineValue = this.getPristineValue();

        this._deviceEditorGroups.setEntitySchemaObject(schemaObject);
        this._deviceEditorGroups.setFormPristineValues(pristineValue);

        return this.renderDeviceForm();
    }

    _onPlatformOptionsUpdated() {
        if (this._model._deviceCategory === "iot" && this._formsComponent) {
            // Save current form values before updating schema
            const formVal = this._formsComponent.getValue();

            // Update schema with new platform options
            const schemaObject = this._getEntitySchemaObject();
            this._deviceEditorGroups.setEntitySchemaObject(schemaObject);

            // Restore form values (not pristine values) to preserve edits
            this._deviceEditorGroups.setFormPristineValues(formVal);

            return this.renderDeviceForm();
        }
        return Promise.resolve();
    }

    _onTagOptionsUpdated() {
        if (!this._formsComponent) {
            return Promise.resolve();
        }
        const formVal = this._formsComponent.getValue();
        formVal.general.tagIds = (formVal.general.tagIds || [])
            .map((tagId) => {
                if (tagId.search(/^__new__/) >= 0) {
                    return this.model._tagOptionsLabelIdMap[tagId.replace(/^__new__/, "")];
                }
                return tagId;
            })
            .filter(Boolean);
        const schemaObject = this._getEntitySchemaObject();
        this._deviceEditorGroups.setEntitySchemaObject(schemaObject);
        this._deviceEditorGroups.setFormPristineValues(formVal);
        return this.renderDeviceForm();
    }

    _isLooselyEqual(a, b) {
        const normalize = (val) => {
            if (val === undefined || val === false || val === "" || Number.isNaN(val)) {
                return null;
            }
            return val;
        };

        return _.isEqual(normalize(a), normalize(b));
    }

    /* No longer used - Case PE-7495 - Sep 2022
    searchProperties(obj, searchKey, results = []) {
        const r = results;
        Object.keys(obj).forEach(key => {
           const value = obj[key];
           if(key === searchKey){
              r.push(value);
           }else if(typeof value === 'object'){
            this.searchProperties(value, searchKey, r);
           }
        });
        return r;
     }
     */

    /*******************************************/
    /*  END PRIVATE API METHODS                */
    /*******************************************/
}

module.exports = DeviceDetailsViewRenderer;
